// Gemini Live API Function Calling Tools
// Format follows Gemini's function calling schema
// Adapted from OpenAI format to Gemini format
// Optimized: id_path hardcoded, room_id auto-generated

import functionCallingServices, { getSafeLanguageId } from '@/utils/services/functionCalling';
import autotourServices from '@/templates/Heritage/utils/services/autotour';
import { queryKnowledgeStream } from '@/agents/shared/knowledgeStreamClient';
import galleryServices from '@/templates/Heritage/utils/services/gallery';
import { activeVisualSourceRegistry } from '@/templates/Heritage/components/ModelLoader/spatial/visual-analysis-contract';
import {
    parseAndNormalizeArtifactId,
    normalizeModelId,
    isTargetingCurrentArtifact,
    executeAndCoordinateToolCalls,
    guardToolResponsesPostExecution,
} from './tool-coordination';
import { hotspotHighlighter } from './krpano-hotspot-highlight';

export {
    parseAndNormalizeArtifactId,
    normalizeModelId,
    isTargetingCurrentArtifact,
    executeAndCoordinateToolCalls,
    guardToolResponsesPostExecution,
};

// ==================== ARTIFACT DOSSIER CACHE & HELPERS ====================
export const ARTIFACT_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
export const artifactDetailsCache = new Map<string, { data: any; timestamp: number }>();

export function clearArtifactDetailsCache(): void {
    artifactDetailsCache.clear();
}

export function sanitizeArtifactText(text: any, maxLength: number = 1500): string {
    if (typeof text !== 'string') return '';
    const cleaned = text
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, ' ')
        .trim();
    if (cleaned.length <= maxLength) return cleaned;
    return cleaned.slice(0, maxLength).trim() + '...';
}

/**
 * Robust multilingual attribute extractor for artifact dossier.
 * Correctly respects the user's requested language:
 * - When English ('en') is requested: searches *_en first, then checks backend translated name/title/description, before falling back to Vietnamese.
 * - When Vietnamese ('vn'/'vi') is requested: searches *_vn / *_vi first.
 * - Reads from attributes (object with .values or string), variables, and direct asset properties.
 */
export function extractMultilingualArtifactField(
    asset: any,
    fieldKeys: string[],
    targetLang: string
): string {
    if (!asset) return '';

    const rawLang = (targetLang || '').trim().toLowerCase();
    const isEnglish = rawLang === 'en' || rawLang === 'english';
    const isVietnamese = rawLang === 'vn' || rawLang === 'vi' || rawLang === 'vietnamese';

    // Prioritized search language tags
    const preferredLangs = isEnglish
        ? ['en']
        : isVietnamese
            ? ['vn', 'vi']
            : [rawLang];
    const fallbackLangs = isEnglish ? ['vn', 'vi'] : ['en', 'vn', 'vi'];

    const getVal = (val: any): string => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'string') {
            const trimmed = val.trim();
            return !/^(null|undefined)$/i.test(trimmed) ? trimmed : '';
        }
        if (typeof val === 'object' && val.values !== undefined) {
            return getVal(val.values);
        }
        return '';
    };

    const readFromSource = (key: string): string => {
        // 1. attributes
        if (asset.attributes && asset.attributes[key] !== undefined) {
            const v = getVal(asset.attributes[key]);
            if (v) return v;
        }
        // 2. variables
        if (asset.variables && asset.variables[key] !== undefined) {
            const v = getVal(asset.variables[key]);
            if (v) return v;
        }
        // 3. direct property on asset
        if (asset[key] !== undefined) {
            const v = getVal(asset[key]);
            if (v) return v;
        }
        return '';
    };

    // 1. First search preferred language tags for all candidate keys (e.g. title_en, content_en, mieu_ta_en)
    for (const l of preferredLangs) {
        for (const key of fieldKeys) {
            const val = readFromSource(`${key}_${l}`);
            if (val) return val;
        }
    }

    // 2. For English/non-VN, check if top-level fields (asset.name, asset.title, asset.description, etc.)
    // were localized by backend API getGalleryById(..., lang)
    if (isEnglish) {
        for (const key of fieldKeys) {
            if (key === 'title' || key === 'name') {
                const directTitle = getVal(asset.title) || getVal(asset.name);
                const titleVn = readFromSource('title_vn') || readFromSource('ten_goi_vn');
                if (directTitle && (!titleVn || directTitle !== titleVn)) {
                    return directTitle;
                }
            }
            if (key === 'description' || key === 'content' || key === 'mieu_ta') {
                const directDesc = getVal(asset.description) || getVal(asset.content);
                const descVn = readFromSource('mieu_ta_vn') || readFromSource('content_vn') || readFromSource('description_vn');
                if (directDesc && (!descVn || directDesc !== descVn)) {
                    return directDesc;
                }
            }
        }
    }

    // 3. Check unlocalized field keys (e.g. title, mieu_ta, content, description)
    for (const key of fieldKeys) {
        const val = readFromSource(key);
        if (val) return val;
    }

    // 4. Check fallback language tags
    for (const l of fallbackLangs) {
        for (const key of fieldKeys) {
            const val = readFromSource(`${key}_${l}`);
            if (val) return val;
        }
    }

    // 5. Final fallback for title/name
    if (fieldKeys.includes('title') || fieldKeys.includes('name')) {
        const fallbackName = getVal(asset.name) || getVal(asset.title) || getVal(asset.attributes?.title?.values);
        if (fallbackName) return fallbackName;
    }

    return '';
}

// ==================== CONSTANTS ====================
// Helper function to generate room ID
const generateRoomId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return `room_${Date.now()}_${crypto.randomUUID()}`;
    }
    return `room_${Date.now()}_${Date.now().toString(36)}`;
};

const handleOpenImageModal = (imageData: any) => {
    try {
        console.log('📸 Opening image modal with data:', imageData);

        if (!imageData || !imageData.uuid) {
            console.warn('⚠️ No image data or uuid available to open modal');
            return { success: false, error: 'No image data available' };
        }

        // Use global function if available
        if (typeof (window as any).handleOpenMaterialDetail === 'function') {
            (window as any).handleOpenMaterialDetail(imageData.uuid, 'photo', imageData.category_uuid, imageData);
        } else {
            if (typeof (window as any).openPanel === 'function') {
                (window as any).openPanel('material');
                setTimeout(() => {
                    if (typeof (window as any).handleOpenMaterialDetail === 'function') {
                        (window as any).handleOpenMaterialDetail(imageData.uuid, 'photo', imageData.category_uuid, imageData);
                    }
                }, 400);
            }
            window.dispatchEvent(
                new CustomEvent('open_image_modal', {
                    detail: { imageData, type: 'photo' },
                })
            );
        }

        return { success: true, message: 'Image modal opened' };
    } catch (error: any) {
        console.error('❌ Error opening image modal:', error);
        return { success: false, error: error.message };
    }
};

const waitForMapFilterResult = (requestId: string, timeoutMs = 2500) => {
    return new Promise<any>((resolve) => {
        if (typeof window === 'undefined') {
            resolve(null);
            return;
        }

        const timeout = window.setTimeout(() => {
            window.removeEventListener('filter_map_projects_result', handleResult as EventListener);
            resolve(null);
        }, timeoutMs);

        const handleResult = (event: Event) => {
            const detail = (event as CustomEvent).detail;
            if (detail?.requestId !== requestId) return;

            window.clearTimeout(timeout);
            window.removeEventListener('filter_map_projects_result', handleResult as EventListener);
            resolve(detail);
        };

        window.addEventListener('filter_map_projects_result', handleResult as EventListener);
    });
};

// Modal handler functions using window global functions
const handleOpenVideoModal = (videoData: any) => {
    try {
        console.log('🎥 Opening video modal with data:', videoData);

        if (!videoData || !videoData.uuid) {
            console.warn('⚠️ No video data or uuid available to open modal');
            return { success: false, error: 'No video data available' };
        }

        // Use global function if available (similar to MaterialContent.tsx approach)
        if (typeof (window as any).handleOpenMaterialDetail === 'function') {
            (window as any).handleOpenMaterialDetail(videoData.uuid, 'video', videoData.category_uuid, videoData);
        } else {
            if (typeof (window as any).openPanel === 'function') {
                (window as any).openPanel('material');
                setTimeout(() => {
                    if (typeof (window as any).handleOpenMaterialDetail === 'function') {
                        (window as any).handleOpenMaterialDetail(videoData.uuid, 'video', videoData.category_uuid, videoData);
                    }
                }, 400);
            }
            window.dispatchEvent(
                new CustomEvent('open_video_modal', {
                    detail: { videoData, type: 'video' },
                })
            );
        }

        return { success: true, message: 'Video modal opened' };
    } catch (error: any) {
        console.error('❌ Error opening video modal:', error);
        return { success: false, error: error.message };
    }
};

// Get current language (with SSR fallback)
const getLanguageId = () => {
    return getSafeLanguageId();
};

// ==================== TYPES ====================
export interface GeminiToolDeclaration {
    name: string;
    description: string;
    parameters: {
        type: "OBJECT";
        properties: Record<string, any>;
        required?: string[];
    };
}

export interface GeminiTool {
    declaration: GeminiToolDeclaration;
    handler: (args: any) => Promise<any>;
}

// ==================== TOOLS DEFINITIONS ====================
const HARDCODED_PRODUCT_FALLBACKS = [
    {
        keywords: ['bột ngọt', 'aji-no-moto', 'ajinomoto', 'msg', 'umami'],
        links: {
            lazada: "https://www.lazada.vn/products/combo-16-4-bot-ngot-aji-no-moto-454g-i982510387-s3110042615.html?spm=a2o4n.seller.list.7.4dfb4351QwHDrN&mp=1",
            tiki: "https://tiki.vn/combo-16-4-bot-ngot-aji-no-moto-454g-p73291856.html?spid=73291857",
            shopee: "https://shopee.vn/Combo-4-B%E1%BB%8Bch-B%E1%BB%99t-Ng%E1%BB%8Dt-AJI-NO-MOTO-454gx4-i.319166665.5765073259",
            shoppe: "https://shopee.vn/Combo-4-B%E1%BB%8Bch-B%E1%BB%99t-Ng%E1%BB%8Dt-AJI-NO-MOTO-454gx4-i.319166665.5765073259",
            sendo: "https://www.sendo.vn/sendofarm"
        }
    },
    {
        keywords: ['matcha', 'blendy', 'trà sữa', 'trà matcha', 'milk tea'],
        links: {
            lazada: "https://www.lazada.vn/products/pdp-i2070462824-s14464043911.html?c=&channelLpJumpArgs=&clickTrackInfo=query%253Ablendy%2525C2%2525AE%252BTr%2525C3%2525A0%252BMatcha%252BS%2525E1%2525BB%2525AFa%253Bnid%253A2070462824%253Bsrc%253ALazadaMainSrp%253Brn%253A158bbe427e28ce6f11289d2ebe1a05d9%253Bregion%253Avn%253Bsku%253A2070462824_VNAMZ%253Bprice%253A64000%253Bclient%253Adesktop%253Bsupplier_id%253A200165430363%253Bsession_id%253A%253Bbiz_source%253Ah5_hp%253Bslot%253A3%253Utlog_bucket_id%253A470687%253Basc_category_id%253A10100557%253Bitem_id%253A2070462824%253Bsku_id%253A14464043911%253Bshop_id%253A1857875%253BtemplateInfo%253A107883_E%2523-1_A3_C%2523&freeshipping=1&fs_ab=2&fuse_fs=&lang=vi&location=Th%C3%A0nh%20ph%E1%BB%91%20H%E1%BB%93%20Ch%C3%AD%20Minh&price=6.4E%204&priceCompare=skuId%3A14464043911%3Bsource%3Alazada-search-voucher%3Bsn%3A158bbe427e28ce6f11289d2ebe1a05d9%3BoriginPrice%3A64000%3BdisplayPrice%3A64000%3BisGray%3Afalse%3BsinglePromotionId%3A-1%3BsingleToolCode%3A-1%3BvoucherPricePlugin%3A0%3Btimestamp%3A1784014077612&ratingscore=4.0&request_id=158bbe427e28ce6f11289d2ebe1a05d9&review=2&sale=15&search=1&source=search&spm=a2o4n.searchlist.list.3&stock=1",
            tiki: "https://tiki.vn/combo-2-hop-tra-matcha-su-a-blendy-170g-p102153003.html?spid=102153004",
            shopee: "https://shopee.vn/Combo-2-H%E1%BB%99p-Tr%C3%A0-Matcha-s%C6%B0%CC%83a-Blendy%C2%AE-160g-H%E1%BB%99p-(1-H%E1%BB%99p-10-G%C3%B3i)-i.319166665.7190351090?sp_atk=8cc6b57c-9c6d-4072-be94-076b2efcc2cf&xptdk=8cc6b57c-9c6d-4072-be94-076b2efcc2cf",
            shoppe: "https://shopee.vn/Combo-2-H%E1%BB%99p-Tr%C3%A0-Matcha-s%C6%B0%CC%83a-Blendy%C2%AE-160g-H%E1%BB%99p-(1-H%E1%BB%99p-10-G%C3%B3i)-i.319166665.7190351090?sp_atk=8cc6b57c-9c6d-4072-be94-076b2efcc2cf&xptdk=8cc6b57c-9c6d-4072-be94-076b2efcc2cf",
            sendo: "https://www.sendo.vn/sendofarm"
        }
    }
];

// Search Promise Cache to decouple Voice Filler generation from Backend Retrieval latency
let pendingKnowledgeSearch: { query: string; promise: Promise<any>; idPath: string } | null = null;
let pendingWebSearch: { query: string; promise: Promise<any>; idPath: string } | null = null;

export function clearPendingKnowledgeSearches() {
    pendingKnowledgeSearch = null;
    pendingWebSearch = null;
}

export const tools: Record<string, GeminiTool> = {
    // ==================== FULL 360° SCENE CAPTURE ====================
    capture_full_scene: {
        declaration: {
            name: "capture_full_scene",
            description: "BẮT BUỘC GỌI khi người dùng hỏi về SỐ LƯỢNG (đếm bao nhiêu người/vật thể trong phòng/không gian: ví dụ 'trong phòng có bao nhiêu người?', 'có mấy cái bàn?', 'đếm...') hoặc hỏi về TỔNG THỂ CẢ CĂN PHÒNG ('trong phòng có những ai/những gì?', 'mô tả toàn bộ căn phòng'). Trích xuất 4 mặt Cubemap chuẩn (Trước 0°, Phải 90°, Sau 180°, Trái 270°) với FOV=90°, ghép thành 1 dải ảnh duy nhất để bao quát 360° không chồng lấn, không điểm mù. Không dùng ảnh góc nhìn hẹp hiện tại để trả lời số lượng của cả phòng.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                // Lazy-load the full-360 capture engine
                const { KrpanoFull360Capture } = await import('./krpano-full-360-capture');
                const { buildFull360SpatialContextText } = await import('./full-360-spatial-context');

                // Singleton — recreate if missing or options drift (HMR can leave a stale Phase-1/2 instance)
                const existing = (window as any).__full360Capturer;
                const needsRecreate = !existing
                    || existing.faceCount !== 4
                    || existing.faceSize !== 512
                    || existing.jpegQuality !== 0.80
                    || existing.captureFov !== 90
                    || existing.__pipeline !== 'origin-34937c954';
                if (needsRecreate) {
                    try { existing?.dispose?.(); } catch { /* ignore */ }
                    const capturerNew = new KrpanoFull360Capture({
                        faceCount: 4,
                        faceSize: 512,
                        jpegQuality: 0.80,
                        captureFov: 90,
                        cooldownMs: 5000,
                        krpanoElementId: 'pano',
                    });
                    (capturerNew as any).__pipeline = 'origin-34937c954';
                    (window as any).__full360Capturer = capturerNew;
                }

                const capturer = (window as any).__full360Capturer as InstanceType<typeof KrpanoFull360Capture>;
                const result = await capturer.captureFullScene(true); // force = true for tool calls

                if (!result) {
                    return {
                        success: false,
                        message: 'Không thể chụp toàn cảnh 360° lúc này. Krpano chưa sẵn sàng hoặc đang xem model 3D.',
                    };
                }

                // Build spatial context for the full scene
                const spatialText = buildFull360SpatialContextText({
                    sceneId: result.sceneId,
                    allHotspots: result.allHotspots,
                    originalCamera: result.originalCamera,
                    faceCount: result.faceCount,
                });

                // Send panorama image immediately so Gemini receives it concurrently with tool response
                let imageSent = false;
                const panoramaBase64 = result.panoramaBase64;
                if (typeof window !== 'undefined' && (window as any).__geminiClientRef) {
                    const client = (window as any).__geminiClientRef;
                    if (client && typeof client.sendImage === 'function') {
                        try {
                            client.sendImage(panoramaBase64, 'image/jpeg');
                            imageSent = true;
                            console.log('[capture_full_scene] Panorama image sent immediately');
                        } catch (e) {
                            console.warn('[capture_full_scene] Could not send image via client:', e);
                        }
                    }
                }

                console.log(`[capture_full_scene] Panorama captured: ${result.width}x${result.height}, ${result.encodedBytes} bytes, ${result.captureTimeMs}ms, ${result.allHotspots.length} hotspots, imageSent=${imageSent}`);

                // Record to diagnostic overlay
                try {
                    const { visualCaptureDiagnostic } = await import('./visual-capture-diagnostic');
                    visualCaptureDiagnostic.recordFull360Capture({
                        enabled: true,
                        captureTimeMs: result.captureTimeMs,
                        encodedBytes: result.encodedBytes,
                    });
                } catch { /* diagnostic is optional */ }

                // IMPORTANT: Tool response must be lightweight text only.
                // The panorama image was already sent via sendImage() above.
                return {
                    success: true,
                    image_sent: imageSent,
                    panorama_info: {
                        width: result.width,
                        height: result.height,
                        faceCount: result.faceCount,
                        sceneId: result.sceneId,
                        hotspotsCount: result.allHotspots.length,
                    },
                    spatial_context: spatialText,
                    // Original instruction (pre first-answer prompt experiments) — verified to reach 7 on panorama read
                    instruction: 'Ảnh panorama 360° toàn cảnh đã được gửi vào kênh video. Dải ảnh gồm 4 phân đoạn liên tiếp theo trục 360° (0° Trước, 90° Phải, 180° Sau, 270° Trái), được đánh dấu bằng label vàng ở trên cùng và ngăn cách bằng đường kẻ vàng đứt nét. '
                    + '4 phân đoạn không chồng lấn (zero overlap), bao quát trọn vẹn 360°. '
                    + 'BẮT BUỘC THỰC HIỆN CHAIN-OF-THOUGHT: '
                    + '(1) Xác định và liệt kê số lượng người THẬT từng ô ①→④. '
                    + '(2) CHỈ đếm người/vật thể THẬT có mặt trong không gian. KHÔNG đếm người/hình trên màn hình TV, poster, tranh ảnh. '
                    + '(3) Người nằm sát đường ranh giới (gần đường kẻ vàng) chỉ đếm 1 lần ở ô nào thấy rõ hơn. '
                    + '(4) Tổng hợp và nói kết quả trực tiếp: "Trong phòng có tổng cộng X người: trước Y, phải Z, sau W, trái V". '
                    + 'TUYỆT ĐỐI KHÔNG NÓI các câu đệm như "Đang kiểm tra...", "Vui lòng đợi...". Hãy nói trực tiếp kết quả.',
                };
            } catch (error: any) {
                console.error('[capture_full_scene] Error:', error);
                return {
                    success: false,
                    message: 'Lỗi khi chụp toàn cảnh 360°.',
                    error: error.message,
                };
            }
        },
    },

    // ==================== PROJECT KNOWLEDGE BASE (RAG & GUARDRAIL) ====================
    // BƯỚC 1: Khởi động tra cứu nội bộ -> Trả về ngay lập tức để AI cất tiếng NÓI ĐỆM KHÚC 1
    search_project_knowledge: {
        declaration: {
            name: "search_project_knowledge",
            description: "Khởi động quy trình tra cứu tài liệu & cơ sở tri thức nội bộ của dự án. BẮT BUỘC gọi tool này khi người dùng hỏi các thông tin dạng câu hỏi/vấn đáp về dự án (tiện ích, thông số, kỹ thuật, quy trình, lịch sử, nội quy). CẢNH BÁO: TUYỆT ĐỐI KHÔNG gọi tool này khi người dùng yêu cầu hành động trực quan như: xem/mở hình ảnh, xem/mở video, xem/mở tài liệu, xem mô hình 3D, mở tab hoặc điều hướng cảnh (ví dụ: 'mở cho tôi hình ảnh...', 'cho xem ảnh...', 'mở video...', 'mở tab hình ảnh', 'dẫn tôi đến...'). Tool này sẽ trả về chỉ dẫn nói câu đệm và bạn BẮT BUỘC gọi tiếp get_search_result ngay sau đó.",
            parameters: {
                type: "OBJECT",
                properties: {
                    query: {
                        type: "STRING",
                        description: "Câu hỏi hoặc từ khóa cần tra cứu trong tài liệu nội bộ dự án",
                    },
                },
                required: ["query"],
            },
        },
        handler: async (args: any) => {
            const rawQ = (args?.query || '').trim().toLowerCase();
            const isMediaAction = /(?:mở|xem|cho xem|phát|chiếu|hiển thị)\s+(?:cho\s+tôi\s+|giúp\s+tôi\s+|mình\s+)?(?:\S+\s+)?(?:hình\s*ảnh|bức\s*ảnh|tấm\s*ảnh|ảnh|hình|video|clip|phim|tài\s*liệu|sách|flipbook|tư\s*liệu|kho\s*tư\s*liệu|module\s*tư\s*liệu|tab|mục)/i.test(rawQ) ||
                                  /^(mở|cho xem|xem|phát)\s+/i.test(rawQ) && /(hình ảnh|ảnh|video|phim|tài liệu|sách|tư liệu|tab)/i.test(rawQ) ||
                                  rawQ.startsWith('mở tab') || rawQ.startsWith('mở hình ảnh') || rawQ.startsWith('mở ảnh') || rawQ.startsWith('mở video') || rawQ.startsWith('mở tư liệu') || rawQ.startsWith('mở module tư liệu');

            if (isMediaAction) {
                if (rawQ.includes('tư liệu') || rawQ.includes('kho tư liệu')) {
                    return { success: false, instruction: "Người dùng đang yêu cầu mở module Tư liệu. Bạn PHẢI gọi tool open_material_panel ngay, không tra cứu tri thức văn bản!" };
                }
                if (rawQ.includes('video')) {
                    return { success: false, instruction: "Người dùng đang yêu cầu mở video. Bạn PHẢI gọi tool open_video ngay, không tra cứu tri thức văn bản!" };
                }
                if (rawQ.includes('tài liệu') || rawQ.includes('sách')) {
                    return { success: false, instruction: "Người dùng đang yêu cầu mở tài liệu. Bạn PHẢI gọi tool open_book ngay, không tra cứu tri thức văn bản!" };
                }
                if (rawQ.includes('tab')) {
                    return { success: false, instruction: "Người dùng đang yêu cầu mở tab tư liệu. Bạn PHẢI gọi tool switch_material_tab ngay, không tra cứu tri thức văn bản!" };
                }
                return { success: false, instruction: "Người dùng đang yêu cầu mở xem hình ảnh trực quan. Bạn PHẢI gọi tool open_photo ngay, không tra cứu tri thức văn bản!" };
            }

            const idPath = typeof window !== 'undefined'
                ? ((window as any).id_path || localStorage.getItem('current_project_id') || sessionStorage.getItem('project_id_path'))
                : undefined;
            if (!idPath) {
                return { success: false, message: 'Không xác định được dự án hiện tại.' };
            }

            // Kích hoạt ngay tìm kiếm trong nền (không chặn luồng nói của AI)
            const searchPromise = queryKnowledgeStream(idPath, args.query, {
                topK: 6,
                enableWebSearch: false
            });
            pendingKnowledgeSearch = { query: args.query, promise: searchPromise, idPath };

            return {
                status: "searching",
                query: args.query,
                instruction: "Hệ thống đang mở tài liệu dự án để tra cứu. BẠN BẮT BUỘC PHẢI NÓI NGAY một câu đệm ngắn bằng chính giọng nói của bạn cho khách nghe (ví dụ: 'Dạ, để em kiểm tra trong tài liệu dự án cho mình nhé ạ!' hoặc 'Dạ em đang tra cứu thông tin trong hồ sơ dự án cho anh/chị đây ạ, đợi em một chút nhé!'), và NGAY LẬP TỨC GỌI TIẾP tool get_search_result với query này để nhận kết quả."
            };
        },
    },

    // BƯỚC 2: Nhận kết quả tra cứu nội bộ (đã chạy song song trong khi AI nói câu đệm Khúc 1)
    get_search_result: {
        declaration: {
            name: "get_search_result",
            description: "Lấy kết quả từ quy trình tra cứu tài liệu nội bộ đã khởi động. BẮT BUỘC gọi tool này ngay sau khi vừa nói xong câu đệm của search_project_knowledge.",
            parameters: {
                type: "OBJECT",
                properties: {
                    query: {
                        type: "STRING",
                        description: "Câu hỏi hoặc từ khóa đã tra cứu",
                    },
                },
                required: ["query"],
            },
        },
        handler: async (args: any) => {
            try {
                const idPath = typeof window !== 'undefined'
                    ? ((window as any).id_path || localStorage.getItem('current_project_id') || sessionStorage.getItem('project_id_path'))
                    : undefined;
                if (!idPath) {
                    return { success: false, message: 'Không xác định được dự án hiện tại.' };
                }

                // Nếu có tìm kiếm đang chạy sẵn từ Bước 1, tái sử dụng Promise; nếu chưa có thì chạy mới
                const data = (pendingKnowledgeSearch && pendingKnowledgeSearch.idPath === idPath)
                    ? await pendingKnowledgeSearch.promise
                    : await queryKnowledgeStream(idPath, args.query, { topK: 6, enableWebSearch: false });
                pendingKnowledgeSearch = null;

                console.log('[get_search_result Gemini] Response:', data);

                if (data?.found && data?.data) {
                    return {
                        success: true,
                        found: true,
                        source: 'knowledge_base',
                        title: data.data.title || data.data.question,
                        content: data.data.content || data.data.answer,
                        url: data.data.url,
                        instruction: `Dữ liệu chính thức từ tài liệu dự án: ${data.data.content || data.data.answer}. Hãy trả lời người dùng một cách chính xác, thân thiện và mạch lạc.`
                    };
                }

                // Nếu nội bộ chưa có và dự án có website Whitelist -> Kích hoạt tìm kiếm web nền và yêu cầu AI nói Khúc 2
                if (data?.need_whitelist_search && data?.allowedDomains && data.allowedDomains.length > 0) {
                    const targetDomain = data.allowedDomains[0];
                    const webSearchPromise = queryKnowledgeStream(idPath, args.query, {
                        topK: 3,
                        onlyWebSearch: true
                    });
                    pendingWebSearch = { query: args.query, promise: webSearchPromise, idPath };

                    return {
                        success: false,
                        found: false,
                        source: 'knowledge_base_miss',
                        need_whitelist_search: true,
                        allowed_domain: targetDomain,
                        instruction: `Trong tài liệu nội bộ dự án chưa có thông tin này. BẮT BUỘC THỰC HIỆN 2 BƯỚC: 1) Bạn PHẢI NÓI NGAY một câu đệm ngắn bằng giọng nói của bạn cho khách nghe: "Dạ câu này em đang tra cứu thêm trên cổng thông tin chính thức của ${targetDomain} cho mình nhé ạ!" 2) NGAY LẬP TỨC GỌI TIẾP tool search_whitelist_web với query này để lấy thông tin.`
                    };
                }

                const contact = data?.fallbackContact;
                const isOutOfScope = Boolean(data?.outOfScope);
                const phone = contact?.link?.replace(/^tel:/i, '') || contact?.contact || contact?.display;
                const dept = contact?.department || 'Ban Quản Lý';
                return {
                    success: false,
                    found: false,
                    outOfScope: isOutOfScope,
                    source: isOutOfScope ? 'out_of_scope' : 'contact_fallback',
                    message: data?.message || (isOutOfScope 
                        ? 'Dạ, câu hỏi này nằm ngoài phạm vi thông tin của dự án.'
                        : 'Dạ, hiện tại em chưa có thông tin chính thức về nội dung này.'),
                    fallbackContact: contact,
                    instruction: isOutOfScope
                        ? (phone 
                            ? `Dạ câu hỏi này nằm ngoài phạm vi thông tin của dự án. Em xin phép gửi thông tin liên hệ tới ${dept} qua số hotline ${phone} để anh/chị được hỗ trợ chu đáo nhất ạ!`
                            : `Dạ câu hỏi này nằm ngoài phạm vi thông tin của dự án. Em xin phép gửi số hotline của ${dept} để anh/chị được hỗ trợ chu đáo nhất ạ!`)
                        : (phone 
                            ? `Dạ hiện tại em chưa có thông tin chính thức về nội dung này. Em xin phép gửi thông tin liên hệ tới ${dept} qua hotline ${phone} để anh/chị được hỗ trợ tận tình nhất ạ!`
                            : "Dạ hiện tại em chưa có thông tin chính thức về nội dung này. Quý khách vui lòng liên hệ Ban Quản Lý để được hỗ trợ tận tình nhất ạ!")
                };
            } catch (error: any) {
                console.error('[get_search_result Gemini] Error:', error);
                return {
                    success: false,
                    message: 'Không thể tra cứu tri thức lúc này.',
                };
            }
        },
    },

    // BƯỚC 3: Nhận kết quả tra cứu website chính thức trong Whitelist
    search_whitelist_web: {
        declaration: {
            name: "search_whitelist_web",
            description:
                "Lấy kết quả tra cứu trên các website chính thức thuộc Whitelist được Chủ Đầu Tư phê duyệt (ví dụ: bsr.com.vn). BẮT BUỘC gọi tool này ngay sau khi vừa nói xong câu đệm của get_search_result.",
            parameters: {
                type: "OBJECT",
                properties: {
                    query: {
                        type: "STRING",
                        description: "Nội dung câu hỏi hoặc từ khóa cần tra cứu trên website chính thức",
                    },
                },
                required: ["query"],
            },
        },
        handler: async (args: any) => {
            try {
                const idPath = typeof window !== 'undefined'
                    ? ((window as any).id_path || localStorage.getItem('current_project_id') || sessionStorage.getItem('project_id_path'))
                    : undefined;
                if (!idPath) {
                    return { success: false, message: 'Không xác định được dự án hiện tại.' };
                }

                // Nếu đã kích hoạt từ Bước 2 thì đợi kết quả; nếu chưa thì chạy mới
                const data = (pendingWebSearch && pendingWebSearch.idPath === idPath)
                    ? await pendingWebSearch.promise
                    : await queryKnowledgeStream(idPath, args.query, { topK: 3, onlyWebSearch: true });
                pendingWebSearch = null;

                console.log('[search_whitelist_web Gemini] Response:', data);

                if (data?.found && data?.data) {
                    const rawUrl = data.data.url || '';
                    let domainHost = '';
                    try {
                        domainHost = new URL(rawUrl).hostname.replace(/^www\./i, '');
                    } catch (_) {
                        domainHost = rawUrl || 'website chính thức';
                    }
                    return {
                        success: true,
                        found: true,
                        source: 'scoped_web_search',
                        title: data.data.title || data.data.question,
                        content: data.data.content || data.data.answer,
                        url: data.data.url,
                        instruction: `Thông tin chính thức từ cổng ${domainHost}: ${data.data.content || data.data.answer}. Hãy trình bày câu trả lời chính xác, rõ ràng và mạch lạc dựa trên nội dung đã tìm thấy.`
                    };
                }

                const contact = data?.fallbackContact;
                const isOutOfScope = Boolean(data?.outOfScope);
                const phone = contact?.link?.replace(/^tel:/i, '') || contact?.contact || contact?.display;
                const dept = contact?.department || 'Ban Quản Lý';
                return {
                    success: false,
                    found: false,
                    outOfScope: isOutOfScope,
                    source: isOutOfScope ? 'out_of_scope' : 'contact_fallback',
                    message: data?.message || (isOutOfScope
                        ? 'Dạ, câu hỏi này nằm ngoài phạm vi thông tin của dự án.'
                        : 'Dạ, hiện tại trên cổng thông tin chính thức cũng chưa có nội dung này.'),
                    fallbackContact: contact,
                    instruction: isOutOfScope
                        ? (phone 
                            ? `Dạ câu hỏi này nằm ngoài phạm vi thông tin của dự án. Em xin phép gửi thông tin liên hệ tới ${dept} qua số hotline ${phone} để anh/chị được hỗ trợ chu đáo nhất ạ!`
                            : `Dạ câu hỏi này nằm ngoài phạm vi thông tin của dự án. Em xin phép gửi số hotline của ${dept} để anh/chị được hỗ trợ chu đáo nhất ạ!`)
                        : (phone 
                            ? `Dạ hiện tại trên cổng thông tin chính thức cũng chưa có nội dung này. Em xin phép gửi thông tin liên hệ tới ${dept} qua hotline ${phone} để anh/chị được hỗ trợ tận tình nhất ạ!`
                            : "Dạ hiện tại em chưa có thông tin chính thức về nội dung này. Quý khách vui lòng liên hệ Ban Quản Lý để được hỗ trợ tận tình nhất ạ!")
                };
            } catch (error: any) {
                console.error('[search_whitelist_web Gemini] Error:', error);
                return {
                    success: false,
                    message: 'Không thể tra cứu trên cổng thông tin chính thức lúc này.',
                };
            }
        },
    },

    // ==================== SCENE MANAGEMENT ====================
    list_scene_names: {
        declaration: {
            name: "list_scene_names",
            description:
                "List all scene names (views, locations, 360-degree panoramas) in the project. Use when user asks: 'What locations are available?', 'Show me all scenes'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    language: {
                        type: "STRING",
                        description: "Language code: 'vn' for Vietnamese, 'en' for English",
                    },
                },
            },
        },
        handler: async (args: any) => {
            const lang = args?.language || getLanguageId() || 'vn';
            const response = await functionCallingServices.listSceneNames(window?.id_path, undefined, undefined, undefined, window?.sublink, lang);
            console.log('[list_scene_names] Full API response:', response);

            // Return only meaningful data to model
            if (response?.data) {
                let limitData = response.data;
                if (Array.isArray(response.data)) {
                    limitData = response.data.slice(0, 10);
                } else if (typeof response.data === 'string') {
                    limitData = response.data.split(';').slice(0, 10).join(';');
                    if (limitData.length > 500) limitData = limitData.substring(0, 500) + '...';
                }
                return { message: response.message || "Scene names retrieved successfully", data: limitData };
            }
            return { message: response?.message || "No scene data available" };
        },
    },

    navigate_to_scene: {
        declaration: {
            name: "navigate_to_scene",
            description:
                "Navigate to a specific scene, hotspot, geographic region (e.g. Miền Bắc, Miền Nam), or play a 360 video (video 360) and provide detailed commentary. Use this tool IMMEDIATELY when the user requests to move or go to any location, OR when they ask to open/play a 360 video (e.g., 'mở video 360 nhà máy đạm', 'dẫn tôi đến Miền Nam'). CRITICAL: Extract ONLY the location or video name, NOT the full user phrase. After navigation, you MUST provide detailed information about the destination. If the API doesn't provide enough information, use Google Search to find details.",
            parameters: {
                type: "OBJECT",
                properties: {
                    scene_query: {
                        type: "STRING",
                        description: "ONLY the location or 360 video name, NOT the full phrase. Extract just the name from user's request. Examples: If user says 'mở video 360 nhà máy đạm', use 'video 360 nhà máy đạm' (keep the word video 360). If user says 'du lịch Tràng An', use 'Tràng An'. If user says 'đi đến chùa Bái Đính', use 'Bái Đính'. Remove all prefixes like 'du lịch', 'đi đến', 'tôi muốn đến', 'dẫn tôi đến', 'mở'. But DO NOT remove 'video 360' if the user wants to see a 360 video, because the actual scene name contains 'video 360'. The name may be a scene, a 360 video, a landmark, a hotspot, or a geographic region — pass it as-is; the system resolves it automatically.",
                    },
                },
                required: ["scene_query"],
            },
        },
        handler: async (args: any) => {
            const response = await functionCallingServices.navigateToScene(
                window?.id_path,
                args.scene_query,
                generateRoomId(),
                getLanguageId(),
                window?.sublink
            );
            console.log('[navigate_to_scene] Full API response:', response);

            // Handle out-of-sublink response with a friendly message
            if (response?.out_of_sublink) {
                return {
                    success: false,
                    message: response.message,
                    instruction: "Inform the user politely that this location is not part of the current project. Suggest they explore other available locations within this project instead. Do NOT attempt to navigate there."
                };
            }

            const isGroupViewOpen = typeof window !== 'undefined' && Boolean((window as any).__isGroupViewOpen);

            // Cập nhật currentSceneInfo ngay lập tức (0ms delay) để title & highlight đồng bộ ngay
            if (response?.data && typeof window !== 'undefined') {
                (window as any).currentSceneInfo = {
                    ...(window as any).currentSceneInfo,
                    ...response.data,
                    name: response.data.name,
                    title: response.data.title || response.data.name,
                    id: response.data.id || response.data.scene_id,
                    group_id: response.data.id_group || response.data.group_id,
                };
            }

            if (!isGroupViewOpen) {
                if (typeof (window as any).resetFunctionStatusToDefault === 'function') {
                    (window as any).resetFunctionStatusToDefault();
                }
            } else {
                // Khi đang ở Khám phá thông tin: không reset sidebar/menu mà chỉ dừng autotour/gallery
                if (typeof (window as any).exitAutotour === 'function') (window as any).exitAutotour();
                if (typeof (window as any).stopTour === 'function') (window as any).stopTour();
                if (typeof (window as any).handleCloseGalleryDetail === 'function') (window as any).handleCloseGalleryDetail();
            }

            setTimeout(() => {
                // Only navigate when the backend returned a scene to move to. On a failed lookup
                // (404) the body has no `data`, so guard before dereferencing `data.name`.
                if (response?.data?.name) {
                    // Use navigateSceneWithPlanetEffect if available (includes Planet → Normal transition)
                    // Otherwise fallback to regular navigateScene
                    if (typeof window.navigateSceneWithPlanetEffect === 'function') {
                        console.log('🌍 Agent: Using navigateSceneWithPlanetEffect with Planet transition');
                        // Pass undefined for blendMode (auto), and dataScene for agent-specific behavior
                        window.navigateSceneWithPlanetEffect(response.data.name, undefined, response.data);
                    } else {
                        // Fallback to regular navigation
                        window.navigateScene('navigate-for-agent', response.data.name, response.data);
                    }
                    window.toggleMute(true);

                    // Nếu panel Khám phá thông tin đang mở hoặc người dùng yêu cầu xem thông tin cảnh: đồng bộ chi tiết nhóm & view tương ứng
                    if (typeof window !== 'undefined') {
                        const queryLower = (args?.scene_query || '').toLowerCase();
                        const wantsInfo = isGroupViewOpen || queryLower.includes('thông tin') || queryLower.includes('khám phá');

                        let groupId = response?.data?.id_group || response?.data?.group_id;
                        if (!groupId) {
                            const targetSceneName = response?.data?.name;
                            const availableGroups: any[] = (window as any).__groupScenes || [];
                            const matched = availableGroups.find((g: any) =>
                                g.scene_home === targetSceneName ||
                                g.first_scene === targetSceneName ||
                                (Array.isArray(g.scenes) && g.scenes.some((s: any) => s.name === targetSceneName || s.scene_id === response?.data?.id)) ||
                                (Array.isArray(g.children) && g.children.some((s: any) => s.name === targetSceneName || s.id === response?.data?.id))
                            );
                            if (matched?.id) {
                                groupId = matched.id;
                            }
                        }

                        if (wantsInfo) {
                            if (typeof (window as any).openPanel === 'function') {
                                (window as any).openPanel('groupView');
                            }
                            if (typeof (window as any).openGroupDetail === 'function') {
                                (window as any).openGroupDetail(groupId ? String(groupId) : undefined, {
                                    skipSceneNavigation: true,
                                    targetSceneName: response?.data?.name,
                                });
                            } else if (groupId && typeof (window as any).openGroupViewAtState === 'function') {
                                (window as any).openGroupViewAtState(window?.id_path, String(groupId), 'group', getLanguageId());
                            }
                        }
                    }
                }
            }, 1000);

            // Check if navigation was unsuccessful - get list of available scenes for suggestions
            if (!response?.success || response?.message?.toLowerCase().includes('could not find') || response?.message?.toLowerCase().includes('not found') || response?.message?.toLowerCase().includes('không tìm thấy')) {
                // Get available scenes to suggest alternatives
                const scenesResponse = await functionCallingServices.listSceneNames(window?.id_path, undefined, undefined, undefined, window?.sublink);
                const rawScenes = scenesResponse?.data;
                const sceneList: any[] = Array.isArray(rawScenes)
                    ? rawScenes
                    : Array.isArray(rawScenes?.items)
                        ? rawScenes.items
                        : Array.isArray(rawScenes?.data)
                            ? rawScenes.data
                            : [];
                const availableScenes: string[] = sceneList
                    .map((s: any) =>
                        typeof s === 'string'
                            ? s
                            : (s?.title || s?.name || s?.scene_name || s?.sceneName || '')
                    )
                    .filter((s: string) => s && s.trim().length > 0);

                if (availableScenes.length > 0) {
                    return {
                        success: false,
                        message: `Không tìm thấy địa điểm "${args.scene_query}". Các địa điểm có thể tham quan: ${availableScenes.join(', ')}.`,
                        suggestion: "Hãy gợi ý người dùng chọn một trong những địa điểm có sẵn ở trên."
                    };
                }
                return {
                    success: false,
                    message: response?.message || `Không tìm thấy địa điểm "${args.scene_query}"`
                };
            }

            // Extract scene information from API response
            const sceneData = response?.data || {};
            const sceneTitle = sceneData?.title || sceneData?.scene_name || args.scene_query;
            const sceneDescription = sceneData?.description || sceneData?.info || '';

            // Determine if we have enough information
            const hasDetailedInfo = sceneDescription && sceneDescription.length > 50;

            return {
                success: true,
                message: response?.message || "Navigation completed",
                scene_info: {
                    title: sceneTitle,
                    description: sceneDescription,
                    full_data: sceneData
                },
                // IMPORTANT: Agent phải thông báo ngay khi chuyển cảnh thành công, không chờ tìm thêm thông tin
                instruction: hasDetailedInfo
                    ? `CRITICAL RESPONSE ORDER: 
                   1. FIRST: Immediately announce "Đã chuyển đến ${sceneTitle}" to the user.
                   2. THEN: After announcing, provide detailed commentary about the location using the information above.
                   Do NOT delay the announcement to search for more information.`
                    : `CRITICAL RESPONSE ORDER:
                   1. FIRST: Immediately announce "Đã chuyển đến ${sceneTitle}" to the user. Do this BEFORE any Google Search.
                   2. THEN: After announcing, you may use Google Search to find more details and share them afterward.
                   NEVER delay the navigation announcement to wait for search results.`
            };
        },
    },

    get_current_scene_info: {
        declaration: {
            name: "get_current_scene_info",
            description:
                "Get info about user's current scene/position and available hotspots. ALWAYS call when user asks: 'Where am I?', 'What scene is this?', or 'What places/attractions are there to visit here?' to check for available hotspots before answering.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            const response = await functionCallingServices.getCurrentSceneInfo(
                window?.id_path,
                generateRoomId(),
                getLanguageId(),
                window?.sublink
            );
            console.log('[get_current_scene_info] Full API response:', response);

            // Return the title from data if available
            if (response?.data?.title) {
                return { message: response.data.title };
            }
            return { message: response?.message || "Scene info retrieved" };
        },
    },

    open_model_tab: {
        declaration: {
            name: "open_model_tab",
            description:
                "Open the 3D model selection interface. Use when user asks to browse/open model gallery.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            // Client-side action - dispatch event
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('open_model_tab'));
                window.openPanel('gallery');
            }
            return { success: true, message: "Model tab opened" };
        },
    },

    // ==================== 3D MODELS ====================
    get_all_model_names: {
        declaration: {
            name: "get_all_model_names",
            description: "Get list of all 3D model names in the project.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            const response = await functionCallingServices.getAllModelNames(window?.id_path, window?.sublink);
            console.log('[get_all_model_names] Full API response:', response);

            // Return data if exists, otherwise return "no data" message
            if (response?.data && response.data !== "No Models found") {
                return { message: response.message || "Model names retrieved successfully", data: response.data };
            }
            return { message: "No models available" };
        },
    },

    open_model: {
        declaration: {
            name: "open_model",
            description:
                "Display a specific 3D model. Use when user wants to view a particular model.",
            parameters: {
                type: "OBJECT",
                properties: {
                    model_query: {
                        type: "STRING",
                        description: "The model name to display (e.g., 'trống đồng', 'bình gốm')",
                    },
                },
                required: ["model_query"],
            },
        },
        handler: async (args: any) => {
            const response = await functionCallingServices.openModel(window?.id_path, args.model_query);
            console.log('[open_model] Full API response:', response);

            let uuid = response?.data?.uuid || response?.uuid;
            if (!uuid) {
                try {
                    const searchRes = await functionCallingServices.searchModelIdByName(window?.id_path, args.model_query);
                    uuid = searchRes?.uuid || searchRes?.data?.uuid;
                } catch (e) {
                    console.warn('[open_model] searchModelIdByName failed:', e);
                }
            }

            window?.resetFunctionStatusToDefault();
            if (typeof window !== 'undefined') {
                (window as any).agentOpenedModel = true;
            }
            if (uuid) {
                window.handleOpenGalleryDetail(window?.id_path, uuid);
                const modelTitle = response?.data?.variables?.title_vn || args.model_query;
                return { success: true, message: `Opened 3D model: ${modelTitle}` };
            }

            let suggestionMsg = "";
            try {
                const modelsRes = await functionCallingServices.getAllModelNames(window?.id_path, window?.sublink);
                if (modelsRes?.data && modelsRes.data !== "No Models found") {
                    const data = modelsRes.data;
                    let list: string[] = [];
                    if (Array.isArray(data)) {
                        list = data.map((m: any) => typeof m === 'string' ? m : (m.title || m.name || m.title_vn || '')).filter(Boolean);
                    } else if (typeof data === 'string') {
                        list = [data];
                    }
                    if (list.length > 0) {
                        suggestionMsg = ` Các mô hình có sẵn: ${list.join(', ')}.`;
                    }
                }
            } catch (e) {}

            return {
                success: false,
                message: (response?.message || `Không tìm thấy mô hình 3D "${args.model_query}".`) + suggestionMsg,
                suggestion: "Hãy báo cho người dùng biết là không tìm thấy mô hình này và gợi ý các mô hình có sẵn trong danh sách."
            };
        },
    },

    open_purchase_link: {
        declaration: {
            name: "open_purchase_link",
            description: "Open the purchase link of a specific model/product on an e-commerce platform (e.g. Lazada, Tiki, Shopee/Shoppe) in a new browser tab.",
            parameters: {
                type: "OBJECT",
                properties: {
                    model_query: {
                        type: "STRING",
                        description: "The product or model name to buy (e.g., 'bột ngọt', 'ajinomoto', 'trà matcha sữa')",
                    },
                    platform: {
                        type: "STRING",
                        description: "The e-commerce platform name. Must be one of: 'lazada', 'tiki', 'shopee', or 'shoppe'.",
                    },
                },
                required: ["model_query", "platform"],
            },
        },
        handler: async (args: any) => {
            const response = await functionCallingServices.openModel(window?.id_path, args.model_query);
            console.log('[open_purchase_link] Model query response:', response);

            let purchaseUrl: string | null = null;

            // 1. Try to extract from API response attributes
            if (response?.data) {
                const attributes = response.data.attributes || response.data.asset?.attributes || {};
                const platformKey = args.platform.toLowerCase();
                let targetKey = platformKey;
                if (platformKey === 'shopee' || platformKey === 'shoppe') {
                    targetKey = attributes.shoppe ? 'shoppe' : 'shopee';
                }
                const url = attributes[targetKey]?.values;
                if (url && url !== 'null' && url.startsWith('http')) {
                    purchaseUrl = url;
                }
            }

            // 2. Fallback to hardcoded list if API doesn't have it (e.g. for matcha tea)
            if (!purchaseUrl) {
                const queryLower = args.model_query.toLowerCase();
                const matchedProduct = HARDCODED_PRODUCT_FALLBACKS.find(product =>
                    product.keywords.some(keyword => queryLower.includes(keyword))
                );

                if (matchedProduct) {
                    const platformKey = args.platform.toLowerCase();
                    const url = (matchedProduct.links as any)[platformKey];
                    if (url) {
                        purchaseUrl = url;
                    }
                }
            }

            if (purchaseUrl) {
                if (typeof window !== 'undefined') {
                    // Check if we are inside an iframe
                    if (window.self !== window.top) {
                        console.log('[open_purchase_link] Inside iframe, delegating to parent window');
                        // Parent window (e.g. Hub) will handle opening the URL via the returned result.
                    } else {
                        window.open(purchaseUrl, '_blank', 'noopener,noreferrer');
                    }
                }
                return { success: true, url: purchaseUrl, message: `Opened ${args.platform} purchase link for ${args.model_query}: ${purchaseUrl}` };
            }

            return {
                success: false,
                message: `Product ${args.model_query} does not have a purchase link on ${args.platform}.`
            };
        },
    },

    // ==================== BOOKS/DOCUMENTS ====================
    get_all_book_names: {
        declaration: {
            name: "get_all_book_names",
            description: "Get list of all books/documents in the project.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            const response = await functionCallingServices.getAllBookNames(window?.id_path);
            console.log('[get_all_book_names] Full API response:', response);

            // Return data if exists, otherwise return "no data" message
            if (response?.data && response.data !== "No Books found") {
                return { message: response.message || "Book names retrieved successfully", data: response.data };
            }
            return { message: "No books available" };
        },
    },

    open_book: {
        declaration: {
            name: "open_book",
            description: "Mở một tài liệu hoặc sách/flipbook cụ thể theo tên hoặc từ khóa (ví dụ: 'mở tài liệu giới thiệu', 'xem sách', 'mở tài liệu bất kì'). TUYỆT ĐỐI KHÔNG gọi search_project_knowledge khi người dùng yêu cầu xem/mở tài liệu.",
            parameters: {
                type: "OBJECT",
                properties: {
                    book_query: {
                        type: "STRING",
                        description: "Tên hoặc từ khóa của tài liệu cần mở (ví dụ: 'tài liệu giới thiệu', 'bất kì').",
                    },
                },
                required: ["book_query"],
            },
        },
        handler: async (args: any) => {
            const rawQuery = (args?.book_query || '').trim().toLowerCase();
            if (rawQuery.includes('tab') || rawQuery === 'tài liệu' || rawQuery === 'sách' || rawQuery.includes('tư liệu')) {
                if (typeof window !== 'undefined') {
                    if (typeof (window as any).openPanel === 'function') {
                        (window as any).openPanel('material');
                    } else if (typeof (window as any).handleOpenMaterial === 'function') {
                        (window as any).handleOpenMaterial();
                    }
                    window.dispatchEvent(new CustomEvent('switch_material_tab', { detail: { tab: 'document' } }));
                    return { success: true, message: "Đã mở tab Tài liệu trong module Tư liệu." };
                }
            }

            const idPath = typeof window !== 'undefined'
                ? ((window as any)?.id_path || (window as any)?.idPath || localStorage.getItem('current_project_id') || localStorage.getItem('id_path'))
                : null;

            const response = await functionCallingServices.openBook(idPath, args.book_query, generateRoomId());
            console.log('[open_book] Full API response:', response);

            let bookData = response?.data;
            if (!bookData || !bookData.uuid) {
                try {
                    const searchRes = await functionCallingServices.searchBookIdByName(idPath, args.book_query);
                    bookData = searchRes?.data || searchRes;
                } catch (e) {
                    console.warn('[open_book] searchBookIdByName failed:', e);
                }
            }

            if (!bookData || !bookData.uuid) {
                return { success: false, message: `Không tìm thấy tài liệu "${args.book_query}". Hãy thử tìm kiếm với tên khác hoặc dùng get_all_book_names để xem danh sách tài liệu có sẵn.` };
            }

            if (typeof window !== "undefined") {
                if (typeof (window as any).handleOpenMaterialDetail === 'function') {
                    (window as any).handleOpenMaterialDetail(bookData.uuid, 'document', bookData.category_uuid, bookData);
                } else {
                    if (typeof (window as any).openPanel === 'function') {
                        (window as any).openPanel('material');
                        setTimeout(() => {
                            if (typeof (window as any).handleOpenMaterialDetail === 'function') {
                                (window as any).handleOpenMaterialDetail(bookData.uuid, 'document', bookData.category_uuid, bookData);
                            }
                        }, 400);
                    }
                    window.dispatchEvent(new CustomEvent('open_book_modal', { detail: { bookData, type: 'document' } }));
                }
            }

            const bookTitle = bookData?.variables?.title_vn || bookData?.title || args.book_query;
            return { success: true, message: `Opened book: ${bookTitle}` };
        },
    },

    // ==================== PHOTOS ====================
    get_all_photo_names: {
        declaration: {
            name: "get_all_photo_names",
            description: "Get list of all photos in the project.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            const idPath = typeof window !== 'undefined'
                ? ((window as any)?.id_path || (window as any)?.idPath || localStorage.getItem('current_project_id') || localStorage.getItem('id_path'))
                : null;
            const response = await functionCallingServices.getAllPhotoNames(idPath);
            console.log('[get_all_photo_names] Full API response:', response);

            // Return data if exists, otherwise return "no data" message
            if (response?.data && response.data !== "No Photos found") {
                return { message: response.message || "Photo names retrieved successfully", data: response.data };
            }
            return { message: "No photos available" };
        },
    },

    open_photo: {
        declaration: {
            name: "open_photo",
            description: "Mở hoặc hiển thị một hình ảnh cụ thể theo tên, từ khóa hoặc khu vực (ví dụ: 'mở cho tôi hình ảnh khu bể chứa dầu thô', 'mở ảnh phân xưởng', 'xem hình tượng phật', 'mở ảnh bất kì', 'show photo'). Sử dụng khi người dùng muốn xem ảnh. TUYỆT ĐỐI KHÔNG gọi search_project_knowledge khi người dùng yêu cầu xem/mở hình ảnh.",
            parameters: {
                type: "OBJECT",
                properties: {
                    photo_query: {
                        type: "STRING",
                        description: "Tên hoặc từ khóa của hình ảnh cần hiển thị (ví dụ: 'khu bể chứa dầu thô', 'phân xưởng', 'bất kì').",
                    },
                },
                required: ["photo_query"],
            },
        },
        handler: async (args: any) => {
            const rawQuery = (args?.photo_query || '').trim().toLowerCase();

            // Nếu người dùng yêu cầu mở tab hình ảnh hoặc tư liệu
            if (rawQuery.includes('tab') || rawQuery === 'hình ảnh' || rawQuery === 'ảnh' || rawQuery.includes('tư liệu')) {
                if (typeof window !== 'undefined') {
                    if (typeof (window as any).openPanel === 'function') {
                        (window as any).openPanel('material');
                    } else if (typeof (window as any).handleOpenMaterial === 'function') {
                        (window as any).handleOpenMaterial();
                    }
                    window.dispatchEvent(new CustomEvent('switch_material_tab', { detail: { tab: 'photo' } }));
                    return { success: true, message: "Đã mở tab Hình ảnh trong module Tư liệu." };
                }
            }

            const relativeKeywords = ['ảnh khác', 'hình khác', 'ảnh tiếp', 'ảnh tiếp theo', 'tiếp theo', 'khác', 'next', 'next photo'];
            if (relativeKeywords.some(k => rawQuery.includes(k))) {
                if (typeof window !== 'undefined' && typeof window.nextPhoto === 'function') {
                    window.nextPhoto();
                    return { success: true, message: "Đã chuyển sang xem hình ảnh tiếp theo." };
                }
            }

            const idPath = typeof window !== 'undefined'
                ? ((window as any)?.id_path || (window as any)?.idPath || localStorage.getItem('current_project_id') || localStorage.getItem('id_path'))
                : null;

            const response = await functionCallingServices.openPhoto(idPath, args.photo_query, generateRoomId());
            console.log('[open_photo] Full API response:', response);

            let photoData = response?.data;
            if (!photoData || !photoData.uuid) {
                try {
                    const searchRes = await functionCallingServices.searchPhotoIdByName(idPath, args.photo_query);
                    photoData = searchRes?.data || searchRes;
                } catch (e) {
                    console.warn('[open_photo] searchPhotoIdByName failed:', e);
                }
            }

            if (!photoData || !photoData.uuid) {
                return { success: false, message: `Không tìm thấy ảnh "${args.photo_query}". Hãy thử tìm kiếm với tên khác hoặc dùng get_all_photo_names để xem danh sách ảnh có sẵn.` };
            }

            handleOpenImageModal(photoData);

            const photoTitle = photoData?.variables?.title_vn || photoData?.title || args.photo_query;
            return { success: true, message: `Opened photo: ${photoTitle}` };
        },
    },

    next_photo: {
        declaration: {
            name: "next_photo",
            description:
                "Chuyển sang xem ảnh tiếp theo hoặc ảnh khác khi đang xem chi tiết ảnh trong Image Viewer. Sử dụng khi người dùng yêu cầu: 'chuyển xem ảnh khác', 'xem ảnh khác', 'ảnh tiếp theo', 'ảnh kế tiếp', 'chuyển ảnh', 'xem hình khác', 'next photo', 'next image'.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            if (typeof window !== 'undefined') {
                if (typeof window.nextPhoto === 'function') {
                    window.nextPhoto();
                    return { success: true, message: "Đã chuyển sang xem hình ảnh tiếp theo." };
                }
                window.dispatchEvent(new CustomEvent('next_photo'));
            }
            return { success: true, message: "Đã chuyển sang ảnh khác." };
        },
    },

    previous_photo: {
        declaration: {
            name: "previous_photo",
            description:
                "Quay lại xem ảnh trước đó khi đang trong trình xem ảnh (Image Viewer). Sử dụng khi người dùng yêu cầu: 'ảnh trước', 'quay lại ảnh trước', 'ảnh lúc nãy', 'previous photo'.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            if (typeof window !== 'undefined') {
                if (typeof window.prevPhoto === 'function') {
                    window.prevPhoto();
                    return { success: true, message: "Đã quay lại ảnh trước đó." };
                }
                window.dispatchEvent(new CustomEvent('prev_photo'));
            }
            return { success: true, message: "Đã quay lại ảnh trước." };
        },
    },

    // ==================== VIDEOS ====================
    get_all_video_names: {
        declaration: {
            name: "get_all_video_names",
            description: "Get list of all videos in the project.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            const response = await functionCallingServices.getAllVideoNames(window?.id_path);
            console.log('[get_all_video_names] Full API response:', response);

            // Return data if exists, otherwise return "no data" message
            if (response?.data && response.data !== "No Videos found") {
                return { message: response.message || "Video names retrieved successfully", data: response.data };
            }
            return { message: "No videos available" };
        },
    },

    open_video: {
        declaration: {
            name: "open_video",
            description: "Mở hoặc phát một video cụ thể theo tên hoặc từ khóa (hoặc chuyển sang tab video). TUYỆT ĐỐI KHÔNG dùng tool này cho video 360 (video 360 phải dùng navigate_to_scene). TUYỆT ĐỐI KHÔNG gọi search_project_knowledge khi người dùng yêu cầu xem/mở video.",
            parameters: {
                type: "OBJECT",
                properties: {
                    video_query: {
                        type: "STRING",
                        description: "Tên hoặc từ khóa của video cần phát (hoặc 'tab video', 'video bất kì')",
                    },
                },
                required: ["video_query"],
            },
        },
        handler: async (args: any) => {
            const rawQuery = (args?.video_query || '').trim().toLowerCase();

            // Nếu người dùng yêu cầu mở tab video
            if (rawQuery.includes('tab') || rawQuery === 'video' || rawQuery === 'clip' || rawQuery.includes('tư liệu')) {
                if (typeof window !== 'undefined') {
                    if (typeof (window as any).openPanel === 'function') {
                        (window as any).openPanel('material');
                    } else if (typeof (window as any).handleOpenMaterial === 'function') {
                        (window as any).handleOpenMaterial();
                    }
                    window.dispatchEvent(new CustomEvent('switch_material_tab', { detail: { tab: 'video' } }));
                    return { success: true, message: "Đã mở tab Video trong module Tư liệu." };
                }
            }

            const relativeKeywords = ['video khác', 'clip khác', 'video tiếp', 'video tiếp theo', 'tiếp theo', 'khác', 'next', 'next video'];
            if (relativeKeywords.some(k => rawQuery.includes(k))) {
                if (typeof window !== 'undefined' && typeof window.nextVideo === 'function') {
                    window.nextVideo();
                    return { success: true, message: 'Đã chuyển sang xem video tiếp theo.' };
                }
            }

            const idPath = typeof window !== 'undefined'
                ? ((window as any)?.id_path || (window as any)?.idPath || localStorage.getItem('current_project_id') || localStorage.getItem('id_path'))
                : null;

            const response = await functionCallingServices.openVideo(idPath, args.video_query);
            console.log('[open_video] Full API response:', response);

            let videoData = response?.data;
            if (!videoData || !videoData.uuid) {
                try {
                    const searchRes = await functionCallingServices.searchVideoIdByName(idPath, args.video_query);
                    videoData = searchRes?.data || searchRes;
                } catch (e) {
                    console.warn('[open_video] searchVideoIdByName failed:', e);
                }
            }

            if (!videoData || !videoData.uuid) {
                return { success: false, message: `Không tìm thấy video "${args.video_query}". Hãy thử tìm kiếm với tên khác hoặc dùng get_all_video_names để xem danh sách video có sẵn.` };
            }

            handleOpenVideoModal(videoData);

            const videoTitle = videoData?.variables?.title_vn || videoData?.title || args.video_query;
            return { success: true, message: `Opened video: ${videoTitle}` };
        },
    },

    // ==================== VIRTUAL TOUR ====================
    create_virtual_tour: {
        declaration: {
            name: "create_virtual_tour",
            description:
                "Create and start a NEW virtual tour with live AI narration. Use ALWAYS when user asks to 'tham quan' (visit/tour), 'tạo tour' (create tour), or wants to visit specific locations with YOUR narration. You MUST narrate the tour guide script aloud to the user as they experience the tour. NOTE: This creates a BRAND NEW tour with AI-generated voice. If the user specifically asks to 'mở autotour có sẵn' or 'phát autotour đã tạo' from the project menu, use start_existing_autotour instead. CRITICAL: Do NOT call this tool when a tour is ALREADY PLAYING and the user simply asks to change narration language (e.g., 'nói tiếng Anh đi', 'continue in English', 'thuyết minh bằng tiếng Nhật'). In that case, just continue narrating in the requested language WITHOUT creating a new tour.",
            parameters: {
                type: "OBJECT",
                properties: {
                    tour_name: {
                        type: "STRING",
                        description: "Name for the tour (e.g., 'Tham quan Tràng An')",
                    },
                    scene_queries: {
                        type: "STRING",
                        description:
                            "Comma-separated location names to include (e.g., 'Hoa Lư, Đền Trình, Hang Múa')",
                    },
                    scenes_per_location: {
                        type: "ARRAY",
                        items: { type: "NUMBER" },
                        description: "Array of random numbers between 5-20 for each location",
                    },
                    narration: {
                        type: "STRING",
                        description:
                            "YOUR complete tour guide narration script that YOU WILL SPEAK ALOUD to the user. Write at least 100 words per location with vivid descriptions, historical facts, and storytelling. This narration will be spoken by you during the tour.",
                    },
                },
                required: ["tour_name", "scene_queries", "scenes_per_location", "narration"],
            },
        },
        handler: async (args: any) => {
            const response = await functionCallingServices.createVirtualTour(
                window?.id_path,
                args.tour_name,
                args.scene_queries,
                args.scenes_per_location,
                generateRoomId(),
                args.narration,
                window?.sublink
            );
            console.log('[create_virtual_tour] Full API response:', response);

            window.resetFunctionStatusToDefault();
            window.toggleMute(true);
            (window as any).__isAiVirtualTourPlaying = true;
            (window as any).cancelPendingVoiceAndStop?.();
            window.startTourFromTourStop(response?.data ? { ...response.data, isAiGenerated: true, suppressSceneVoice: true } : response?.data);

            // Return instruction for agent to narrate
            return {
                message: `Tour "${args.tour_name}" has been created and started. NOW SPEAK YOUR NARRATION ALOUD: "${args.narration}"`,
                instruction: "You must now narrate the tour guide script you created in the 'narration' parameter. Speak it aloud with enthusiasm and emotion as the user experiences the tour."
            };
        },
    },

    stop_virtual_tour: {
        declaration: {
            name: "stop_virtual_tour",
            description:
                "Stop and exit the currently playing virtual tour. Use IMMEDIATELY when the user asks to stop, exit, cancel, end, or quit the tour (e.g., 'dừng tour đi', 'thoát tour', 'stop the tour', 'kết thúc tour', 'hủy tour', 'không xem tour nữa'). This will stop all auto-navigation and return the user to normal viewing mode.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                // Call the global stopTour function exposed by useAutoTourData hook
                if (typeof window !== 'undefined') {
                    if (typeof window.stopTour === 'function') {
                        window.stopTour();
                    }
                    if (typeof (window as any).exitAutotour === 'function') {
                        (window as any).exitAutotour();
                    }
                }
                try {
                    const { tourRecorder } = await import('@/utils/tour-recording/tour-recorder');
                    if (tourRecorder.isRecording()) {
                        tourRecorder.stopRecording().then(async (result) => {
                            if (result) {
                                const { uploadTourRecording } = await import('@/utils/tour-recording/tour-recording-uploader');
                                uploadTourRecording(result).catch((e) => console.error('[stop_virtual_tour] Error auto-uploading recording:', e));
                            }
                        });
                    }
                } catch {
                    // Ignore recording check error
                }
                return {
                    success: true,
                    message: "Tour đã được dừng thành công. Người dùng đã quay lại chế độ xem bình thường.",
                    instruction: "Thông báo cho người dùng rằng tour đã dừng và hỏi họ muốn làm gì tiếp theo (ví dụ: xem cảnh khác, tạo tour mới, hoặc khám phá tự do)."
                };
            } catch (error: any) {
                console.error('❌ Error stopping tour:', error);
                return { success: false, error: error.message };
            }
        },
    },

    // ==================== TOUR RECORDING ====================
    start_tour_recording: {
        declaration: {
            name: "start_tour_recording",
            description:
                "Bắt đầu ghi hình chuyến AutoTour đang chạy thành video. CHỈ SỬ DỤNG KHI ĐANG CHẠY AUTOTOUR (hoặc người dùng nói rõ 'quay lại autotour này'). Nếu người dùng đang khám phá tự do bên ngoài AutoTour và yêu cầu 'quay màn hình', 'quay video tương tác AI', 'ghi hình cuộc trò chuyện', hãy gọi tool `start_web_interaction_recording` thay vì tool này.",
            parameters: {
                type: "OBJECT",
                properties: {
                    tour_name: {
                        type: "STRING",
                        description: "Tên tour đang ghi hình (ví dụ: 'Tham quan Tràng An')"
                    }
                }
            }
        },
        handler: async (args: any) => {
            try {
                const { tourRecorder } = await import('@/utils/tour-recording/tour-recorder');
                const idPath = typeof window !== 'undefined' ? (window as any).id_path || '' : '';
                const tourName = args?.tour_name || 'AI Virtual Tour';

                const started = await tourRecorder.startRecording({
                    tourName,
                    idPath
                });

                if (started) {
                    return {
                        success: true,
                        message: `Đang ghi hình tour "${tourName}". Chấm đỏ REC đang hiển thị trên màn hình.`,
                        instruction: "Xác nhận với người dùng rằng bạn đang ghi hình tour và tiếp tục thuyết minh."
                    };
                } else {
                    return {
                        success: false,
                        message: "Không thể bắt đầu ghi hình tour trên thiết bị này."
                    };
                }
            } catch (error: any) {
                console.error('❌ Error starting tour recording:', error);
                return { success: false, error: error.message };
            }
        }
    },

    stop_tour_recording: {
        declaration: {
            name: "stop_tour_recording",
            description:
                "Dừng ghi hình tour và lưu video vào hệ thống tài nguyên (Material). Sử dụng khi người dùng yêu cầu dừng ghi hình (ví dụ: 'dừng ghi hình', 'stop recording', 'lưu video tour lại', 'ngừng quay').",
            parameters: {
                type: "OBJECT",
                properties: {}
            }
        },
        handler: async () => {
            try {
                const { tourRecorder } = await import('@/utils/tour-recording/tour-recorder');
                const { webInteractionRecorder } = await import('@/utils/web-recording/web-interaction-recorder');
                if (webInteractionRecorder.getStatus() === 'recording') {
                    await webInteractionRecorder.stopRecording();
                    return {
                        success: true,
                        message: "Đã dừng ghi hình tương tác AI và đang tải video MP4 lên máy chủ.",
                        instruction: "Thông báo cho người dùng rằng video tương tác AI đang được lưu và sẽ hiển thị cửa sổ xem lại/chia sẻ ngay."
                    };
                }
                if (!tourRecorder.isRecording()) {
                    return {
                        success: false,
                        message: "Hiện tại không có tour nào đang được ghi hình."
                    };
                }

                const result = await tourRecorder.stopRecording();
                if (result) {
                    const { uploadTourRecording } = await import('@/utils/tour-recording/tour-recording-uploader');
                    uploadTourRecording(result).catch((e) => console.error('[stop_tour_recording] Upload error:', e));

                    return {
                        success: true,
                        message: "Đã dừng ghi hình. Video tour đang được lưu và sẽ hiển thị popup chia sẻ cho người dùng.",
                        instruction: "Thông báo cho người dùng rằng video tour đang được lưu và họ có thể chia sẻ video ngay."
                    };
                } else {
                    return {
                        success: false,
                        message: "Video tour chưa đủ độ dài tối thiểu (dưới 5 giây) nên không được lưu."
                    };
                }
            } catch (error: any) {
                console.error('❌ Error stopping tour recording:', error);
                return { success: false, error: error.message };
            }
        }
    },

    // ==================== WEB & AI INTERACTION RECORDING ====================
    start_web_interaction_recording: {
        declaration: {
            name: "start_web_interaction_recording",
            description:
                "Mở hộp thoại xác nhận Micro và bắt đầu ghi hình toàn bộ trang web & tương tác trực tiếp với AI Agent (ngoài phạm vi AutoTour). Sử dụng khi người dùng yêu cầu: 'quay màn hình cho tôi', 'ghi hình tương tác AI', 'quay lại cuộc trò chuyện này', 'quay video trang web', hoặc 'ghi hình lại đoạn này'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    title: {
                        type: "STRING",
                        description: "Tiêu đề gợi ý cho video tương tác AI (ví dụ: 'Tương tác AI - Khám phá Nhà máy')"
                    }
                }
            }
        },
        handler: async (args: any) => {
            try {
                if (typeof window !== 'undefined' && (window as any).__aiInteractionRecordingEnabled === false) {
                    return {
                        success: false,
                        message: "Tính năng ghi hình tương tác AI hiện đang được tạm tắt bởi Quản trị viên dự án.",
                        instruction: "Thông báo lịch sự cho người dùng rằng tính năng ghi hình tương tác AI hiện đang tạm tắt trong cấu hình dự án."
                    };
                }
                const { webInteractionRecorder } = await import('@/utils/web-recording/web-interaction-recorder');
                if (webInteractionRecorder.getStatus() === 'recording') {
                    return {
                        success: true,
                        message: "Hệ thống đang trong quá trình ghi hình tương tác AI rồi.",
                        instruction: "Thông báo cho người dùng rằng màn hình tương tác đang được ghi hình."
                    };
                }
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(
                        new CustomEvent('open-web-recording-mic-modal', {
                            detail: { title: args?.title }
                        })
                    );
                }
                return {
                    success: true,
                    message: "Đã hiển thị hộp thoại hỏi bật/tắt Micro trên màn hình để người dùng xác nhận bắt đầu quay.",
                    instruction: "Thông báo cho người dùng rằng bạn đã mở bảng xác nhận ghi hình trên màn hình, mời họ chọn Bật hoặc Không bật Micro để bắt đầu quay."
                };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        }
    },

    stop_web_interaction_recording: {
        declaration: {
            name: "stop_web_interaction_recording",
            description:
                "Dừng ghi hình toàn trang web / tương tác AI Agent và lưu thành video MP4 để xem lại hoặc chia sẻ. Sử dụng khi người dùng yêu cầu: 'dừng quay màn hình', 'dừng ghi hình tương tác', 'ngừng quay video', 'lưu video lại'.",
            parameters: {
                type: "OBJECT",
                properties: {}
            }
        },
        handler: async () => {
            try {
                const { webInteractionRecorder } = await import('@/utils/web-recording/web-interaction-recorder');
                if (webInteractionRecorder.getStatus() === 'recording') {
                    await webInteractionRecorder.stopRecording();
                    return {
                        success: true,
                        message: "Đã dừng ghi hình tương tác AI. Video đang được xử lý sang định dạng MP4 và sẽ hiển thị popup chia sẻ ngay.",
                        instruction: "Thông báo cho người dùng rằng bạn đã dừng ghi hình tương tác và video đang được lưu để họ xem lại hoặc chia sẻ."
                    };
                }
                const { tourRecorder } = await import('@/utils/tour-recording/tour-recorder');
                if (tourRecorder.isRecording()) {
                    const result = await tourRecorder.stopRecording();
                    if (result) {
                        const { uploadTourRecording } = await import('@/utils/tour-recording/tour-recording-uploader');
                        uploadTourRecording(result).catch((e) => console.error('[stop_web_interaction_recording] Upload error:', e));
                    }
                    return {
                        success: true,
                        message: "Đã dừng ghi hình AutoTour đang chạy.",
                        instruction: "Thông báo cho người dùng rằng đã dừng ghi hình và video đang được lưu."
                    };
                }
                return {
                    success: false,
                    message: "Hiện tại không có phiên ghi hình nào đang chạy."
                };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        }
    },

    // ==================== EXISTING AUTOTOUR (pre-made tours with saved voice) ====================
    list_existing_autotours: {
        declaration: {
            name: "list_existing_autotours",
            description:
                "Lấy danh sách các AutoTour ĐÃ ĐƯỢC TẠO SẴN trong dự án (tour có voice/audio đã thu sẵn). Sử dụng khi người dùng hỏi 'có autotour nào?', 'danh sách autotour', hoặc khi cần kiểm tra autotour nào đã tồn tại TRƯỚC KHI phát bằng start_existing_autotour. LƯU Ý: Tool này dành cho autotour đã tạo sẵn trong hệ thống, KHÔNG liên quan đến việc tạo tour mới.",
            parameters: {
                type: "OBJECT",
                properties: {
                    page: {
                        type: "STRING",
                        description: "Số trang (mặc định '1')",
                    },
                    limit: {
                        type: "STRING",
                        description: "Số lượng tour mỗi trang (mặc định '20')",
                    },
                },
            },
        },
        handler: async (args: any) => {
            try {
                const idPath = window?.id_path;
                if (!idPath) {
                    return { success: false, error: 'Không tìm thấy id_path của dự án.' };
                }
                const page = args?.page || '1';
                const limit = args?.limit || '20';
                const result = await autotourServices.getProcessedAutotours(idPath, page, limit, '', '1');
                if (result.tours.length === 0) {
                    return {
                        success: true,
                        tours: [],
                        message: 'Dự án hiện chưa có AutoTour nào được tạo sẵn.',
                        instruction: 'Thông báo cho người dùng rằng dự án chưa có autotour nào. Gợi ý họ có thể yêu cầu bạn tạo tour mới bằng cách nói "tạo tour" hoặc "tham quan".'
                    };
                }
                const tourList = result.tours.map(t => ({
                    id: t.id,
                    title: t.title,
                    count_scene: t.count_scene,
                    total_time: t.total_time,
                }));
                return {
                    success: true,
                    tours: tourList,
                    total: result.pagination?.total || result.tours.length,
                    message: `Tìm thấy ${tourList.length} AutoTour có sẵn.`,
                    instruction: 'Liệt kê tên các autotour cho người dùng và hỏi họ muốn phát tour nào. Khi họ chọn, gọi start_existing_autotour với tour_id tương ứng.'
                };
            } catch (error: any) {
                console.error('❌ Error listing existing autotours:', error);
                return { success: false, error: error.message };
            }
        },
    },

    start_existing_autotour: {
        declaration: {
            name: "start_existing_autotour",
            description:
                "Phát/mở một AutoTour CÓ SẴN trong dự án theo ID. Tour sẽ tự động chạy với voice/audio đã được tạo trước đó — KHÔNG tự tạo narration mới. Sử dụng khi người dùng yêu cầu 'mở autotour có sẵn', 'phát autotour X', 'bật autotour', 'chạy autotour đã tạo', 'play existing autotour'. PHẢI gọi list_existing_autotours trước để lấy đúng tour_id. LƯU Ý QUAN TRỌNG: KHÔNG dùng tool này khi người dùng nói 'tham quan' hoặc 'tạo tour' — những trường hợp đó PHẢI dùng create_virtual_tour.",
            parameters: {
                type: "OBJECT",
                properties: {
                    tour_id: {
                        type: "NUMBER",
                        description: "ID của autotour cần phát (lấy từ list_existing_autotours)",
                    },
                    tour_name: {
                        type: "STRING",
                        description: "Tên tour (để xác nhận với người dùng)",
                    },
                    lang: {
                        type: "STRING",
                        description: "Mã ngôn ngữ (ví dụ: 'vn', 'en'). Mặc định theo ngôn ngữ hiện tại của dự án.",
                    },
                },
                required: ["tour_id"],
            },
        },
        handler: async (args: any) => {
            try {
                const tourId = args.tour_id;
                const tourName = args.tour_name || `Tour #${tourId}`;
                const lang = args.lang;

                if (typeof window === 'undefined') {
                    return { success: false, error: 'Window not available.' };
                }

                // Use the global startAutotour function exposed by useAutoTourData hook
                // which fetches tour details (including audio) and plays the tour with its saved voice
                if (typeof window.startAutotour === 'function') {
                    window.resetFunctionStatusToDefault?.();
                    // Khong tat tieng vi autotour co voice thuyet minh rieng
                    (window as any).__isAiVirtualTourPlaying = false;
                    (window as any).__isPreMadeAutoTourRunning = true;
                    (window as any).userHasInteracted = true;
                    if (typeof window.toggleMute === 'function' && window.isMuted) {
                        window.toggleMute(false);
                    }
                    const krpano = (window as any).krpano;
                    if (krpano?.call) {
                        try {
                            krpano.call('resumesoundall();');
                        } catch (e) {}
                    }
                    await window.startAutotour(tourId, lang);
                    return {
                        success: true,
                        message: `AutoTour "${tourName}" đang được phát với voice đã tạo sẵn.`,
                        instruction: `AutoTour "${tourName}" đã bắt đầu phát với audio/voice có sẵn. Thông báo ngắn gọn cho người dùng rằng tour đang chạy. KHÔNG cần thuyết minh — tour này đã có voice riêng. Nếu người dùng muốn dừng, hãy dùng stop_virtual_tour.`
                    };
                }

                return {
                    success: false,
                    error: 'Chức năng startAutotour chưa sẵn sàng. Hãy thử lại sau.',
                    instruction: 'Thông báo cho người dùng rằng chức năng phát autotour chưa sẵn sàng và gợi ý họ thử lại.'
                };
            } catch (error: any) {
                console.error('❌ Error starting existing autotour:', error);
                return { success: false, error: error.message };
            }
        },
    },

    // ==================== PROJECT INFO ====================
    get_project_name_by_id: {
        declaration: {
            name: "get_project_name_by_id",
            description: "Get the project name.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            const response = await functionCallingServices.getProjectNameById(window?.id_path, window?.sublink);
            console.log('[get_project_name_by_id] Full API response:', response);

            // Return project name from data
            if (response?.data) {
                return { message: `Project name: ${response.data}` };
            }
            return { message: response?.message || "Project information retrieved" };
        },
    },

    // ==================== IoT / FIRE ALARM ====================
    show_iot_monitoring_dashboard: {
        declaration: {
            name: "show_iot_monitoring_dashboard",
            description:
                "Open and display the IoT real-time monitoring dashboard (sensor data, flow rate, pressure, temperature, volume charts) or summarize building IoT status (QTSC 3 building, room occupancy, active issues, inspection status). Use when the user asks to view IoT data, sensor readings, monitoring dashboard, building status summary, room occupancy, or live equipment data (e.g., 'tóm tắt nhanh tình trạng của tòa nhà QTSC 3', 'có phòng nào bận không', 'có issue nào mới không', 'mở thông tin iot').",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                const currentIdPath = (typeof window !== 'undefined')
                    ? ((window as any)?.id_path || (window as any)?.idPath)
                    : null;

                (window as any)?.IotControlPopup?.closePopup?.();

                if (currentIdPath === '20251210') {
                    (window as any)?.IotControlPopup?.openPopup?.('iot-dashboard');
                    const narrationVn = "Chào bạn. Hệ thống IoT vừa ghi nhận một Issue khẩn: Rò rỉ nước tại Phòng Kỹ thuật. Phòng họp Boardroom đang bận tới 11h, hành lang Bắc đã kiểm tra lúc 8h.";
                    const narrationEn = "Hello. The IoT system just recorded an urgent issue: Water leakage in the Technical/HVAC Room. The Boardroom is busy until 11 AM, and the North Corridor was checked at 8 AM.";
                    return {
                        success: true,
                        message: `IoT monitoring dashboard for project ${currentIdPath} displayed.`,
                        instruction: `Confirm to the user in the EXACT SAME LANGUAGE they spoke. Vietnamese: "${narrationVn}", English: "${narrationEn}"`,
                        narration_vn: narrationVn,
                        narration_en: narrationEn,
                    };
                } else if (currentIdPath === '20260812') {
                    (window as any)?.IotControlPopup?.openPopup?.('iot-dashboard');
                    const narrationVn = "Chào bạn. Hệ thống IoT vừa ghi nhận một Issue khẩn: Rò rỉ nước tại Không gian làm việc chung. Phòng họp Boardroom đang bận tới 11h, hành lang Bắc đã kiểm tra lúc 8h.";
                    const narrationEn = "Hello. The IoT system just recorded an urgent issue: Water leakage in the Co-working space. The Boardroom is busy until 11 AM, and the North Corridor was checked at 8 AM.";
                    return {
                        success: true,
                        message: `IoT monitoring dashboard for project ${currentIdPath} displayed.`,
                        instruction: `Confirm to the user in the EXACT SAME LANGUAGE they spoke. Vietnamese: "${narrationVn}", English: "${narrationEn}"`,
                        narration_vn: narrationVn,
                        narration_en: narrationEn,
                    };
                } else {
                    (window as any)?.IotControlPopup?.openIoTChart?.();
                    return { success: true, message: "IoT monitoring dashboard displayed successfully" };
                }
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    show_cctv_list: {
        declaration: {
            name: "show_cctv_list",
            description: "Open the IoT dashboard and display the CCTV/Camera list in grid view (showing all areas/zones). Use when the user asks to view all cameras, CCTV list, or system cameras.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                (window as any)?.IotControlPopup?.showCctvList?.();
                return { success: true, message: "CCTV list displayed successfully" };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    show_specific_cctv: {
        declaration: {
            name: "show_specific_cctv",
            description: "Open a specific CCTV/Camera by room or area name. Use when the user asks to view the CCTV of a specific area (e.g., 'Tôi muốn xem CCTV tại phòng này', 'Mở camera phân khu 1', 'Camera phòng họp').",
            parameters: {
                type: "OBJECT",
                properties: {
                    area_name: {
                        type: "STRING",
                        description: "The name of the area to view the camera for (e.g., 'Phân khu 1', 'Phòng họp', 'Không gian làm việc chung')."
                    }
                },
                required: ["area_name"]
            },
        },
        handler: async (args: { area_name: string }) => {
            try {
                let area = args.area_name.toLowerCase();
                
                // Nếu người dùng nói chung chung "phòng này", "khu này", "chỗ này", ta sẽ lấy vị trí hiện tại từ krpano (nếu có)
                if (area.includes('này') || area === 'đây' || area.includes('hiện tại') || area === 'phòng' || area === 'khu vực') {
                    const currentSceneTitle = (window as any)?.krpano?.get?.("scene[get(xml.scene)].title") || "";
                    const currentSceneGroup = (window as any)?.krpano?.get?.("scene[get(xml.scene)].group") || "";
                    const currentSceneAlbum = (window as any)?.krpano?.get?.("scene[get(xml.scene)].album") || "";
                    area = (currentSceneTitle + " " + currentSceneGroup + " " + currentSceneAlbum).toLowerCase();
                }

                let index = 1; // Default
                if (area.includes('1') || area.includes('làm việc chung') || area.includes('lối đi (tầng 3)')) index = 1;
                else if (area.includes('2') || area.includes('doanh nghiệp') || area.includes('sảnh chung')) index = 2;
                else if (area.includes('3') || area.includes('hỗ trợ') || area.includes('văn phòng đại diện quỹ đầu tư (1)')) index = 3;
                else if (area.includes('4') || area.includes('trình diễn') || area.includes('đường dẫn vào phân khu 4')) index = 4;
                else if (area.includes('6') || area.includes('lưu trú') || area.includes('phân khu 6')) index = 6;
                else index = 1; // Default to 1 if unknown or 'phòng họp'

                (window as any)?.IotControlPopup?.showSpecificCctv?.(index);
                return { success: true, message: `CCTV for ${args.area_name} displayed successfully` };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    show_fire_emergency_system: {
        declaration: {
            name: "show_fire_emergency_system",
            description:
                "Open the fire alarm camera / emergency system view to show the fire alert status and camera footage. Use when the user asks about fire alarm, fire detection camera, emergency alert, or fire safety system. AFTER calling this tool, you MUST immediately read the evacuation instructions aloud with urgency.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                const currentIdPath = (typeof window !== 'undefined')
                    ? ((window as any)?.id_path || (window as any)?.idPath)
                    : null;

                (window as any)?.IotControlPopup?.closePopup?.();
                (window as any)?.IotControlPopup?.showFireAlarm?.();

                let evacuationScriptEn = "Attention! Fire detected at Maindeck – Center. All personnel, stop work immediately. Leave the area now. Follow emergency exit signs. Do not run. Cover your nose and mouth. Stay low if smoke is present. Proceed to your designated Muster Station. This is not a drill.";
                let evacuationScriptVn = "Chú ý! Đã phát hiện cháy tại khu vực Maindeck. Yêu cầu tất cả nhân sự ngừng làm việc ngay lập tức và rời khỏi khu vực. Di chuyển theo biển báo lối thoát hiểm. Không chạy. Dùng khăn che mũi và miệng. Đi khom thấp nếu có khói. Di chuyển đến điểm tập kết được chỉ định. Đây không phải là diễn tập.";

                // Tùy biến lời thoại báo cháy cho dự án NIC
                if (currentIdPath === '20260812') {
                    evacuationScriptEn = "Attention! Fire detected at the Co-working space. All personnel, stop work immediately. Leave the area now. Follow emergency exit signs. Proceed to the nearest exit. This is not a drill.";
                    evacuationScriptVn = "Chú ý! Hệ thống vừa ghi nhận sự cố rò rỉ và nguy cơ cháy tại Không gian làm việc chung. Yêu cầu tất cả nhân sự ngừng làm việc ngay lập tức và rời khỏi khu vực. Di chuyển theo biển báo lối thoát hiểm. Đây không phải là diễn tập.";
                }

                return {
                    success: true,
                    message: `Fire alarm camera activated.`,
                    instruction:
                        `You MUST now read the evacuation announcement aloud in a clear, urgent voice so all personnel can hear it. Use the user's language. Vietnamese: "${evacuationScriptVn}", English: "${evacuationScriptEn}"`,
                    evacuationScriptEn,
                    evacuationScriptVn,
                };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_iot_monitoring_dashboard: {
        declaration: {
            name: "close_iot_monitoring_dashboard",
            description:
                "Close or hide the IoT real-time monitoring dashboard. Use when the user asks to close, dismiss, hide, turn off, or exit the IoT dashboard, IoT monitoring panel, sensor dashboard, live equipment data view, or realtime chart.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                (window as any)?.IotControlPopup?.closePopup?.();
                return { success: true, message: "IoT monitoring dashboard closed", instruction: "Thông báo cho người dùng rằng bạn đã đóng bảng giám sát IoT." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_fire_emergency_system: {
        declaration: {
            name: "close_fire_emergency_system",
            description:
                "Close or hide the fire alarm camera / emergency system view. Use when the user asks to close, dismiss, hide, turn off, or exit the fire alarm camera, fire emergency system, fire alert video, emergency alert, or fire safety popup.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                (window as any)?.IotControlPopup?.closePopup?.();
                return { success: true, message: "Fire alarm camera closed", instruction: "Thông báo cho người dùng rằng bạn đã đóng camera cảnh báo cháy." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    // ==================== MATERIAL MODULE CONTROLS ====================
    open_material_panel: {
        declaration: {
            name: "open_material_panel",
            description:
                "Mở module Tư liệu (Material) để xem hình ảnh, video, tài liệu của dự án. Sử dụng khi người dùng nói: 'mở tư liệu', 'xem tư liệu', 'show materials', 'mở kho hình', 'xem ảnh dự án', 'cho xem hình ảnh video'. Nếu module Tư liệu đã mở sẵn thì KHÔNG cần gọi lại. Lưu ý: Để chuyển tab cụ thể (hình ảnh/video/tài liệu), dùng switch_material_tab.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined") {
                    if (typeof window.openPanel === "function") {
                        window.openPanel("material");
                        return { success: true, message: "Đã mở module Tư liệu. Các tab có sẵn: Hình ảnh (photo), Video (video), Tài liệu (document).", instruction: "PHẢI thông báo cho người dùng rằng bạn đã mở module Tư liệu và giới thiệu ngắn gọn các tab có sẵn." };
                    }
                    if (typeof (window as any).handleOpenMaterial === "function") {
                        (window as any).handleOpenMaterial();
                        return { success: true, message: "Đã mở module Tư liệu. Các tab có sẵn: Hình ảnh (photo), Video (video), Tài liệu (document).", instruction: "PHẢI thông báo cho người dùng rằng bạn đã mở module Tư liệu và giới thiệu ngắn gọn các tab có sẵn." };
                    }
                }
                return { success: false, message: "Không thể mở module Tư liệu - chức năng chưa sẵn sàng.", instruction: "Thông báo cho người dùng rằng không thể mở module Tư liệu." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_material_panel: {
        declaration: {
            name: "close_material_panel",
            description:
                "Đóng module Tư liệu (Material) đang mở. Sử dụng khi người dùng nói: 'đóng tư liệu', 'tắt tư liệu', 'đóng kho hình', 'close materials'. KHÔNG dùng close_tab cho việc này — close_tab chỉ đóng viewer 3D/video/ảnh.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.closeDrawerMenu === "function") {
                    window.closeDrawerMenu("material");
                    return { success: true, message: "Đã đóng module Tư liệu.", instruction: "Thông báo cho người dùng rằng bạn đã đóng module Tư liệu." };
                }
                return { success: false, message: "Không thể đóng module Tư liệu." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    switch_material_tab: {
        declaration: {
            name: "switch_material_tab",
            description:
                "Chuyển đổi giữa các tab trong module Tư liệu: Hình ảnh (photo), Video (video), Tài liệu (document). Sử dụng khi người dùng muốn xem loại tư liệu cụ thể, ví dụ: 'chuyển sang tab video', 'cho xem ảnh', 'xem hình ảnh', 'mở video', 'xem video đi', 'mở tài liệu', 'chuyển qua ảnh', 'chuyển qua video', 'mở phần tài liệu', 'switch to photos', 'show videos'. Nếu module Tư liệu chưa mở, sẽ tự động mở trước khi chuyển tab. QUAN TRỌNG: Khi người dùng nói 'ảnh'/'hình'/'hình ảnh'/'photos' → tab='photo'; nói 'video'/'clip' → tab='video'; nói 'tài liệu'/'văn bản'/'documents' → tab='document'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    tab: {
                        type: "STRING",
                        description: "Tab cần chuyển đến. Chỉ chấp nhận: 'photo' (Hình ảnh), 'video' (Video), 'document' (Tài liệu). Mapping: hình ảnh/ảnh/photos → 'photo', video/clip → 'video', tài liệu/văn bản/documents → 'document'.",
                        enum: ["photo", "video", "document"],
                    },
                },
                required: ["tab"],
            },
        },
        handler: async (args: any) => {
            try {
                const validTabs = ["photo", "video", "document"];
                if (!validTabs.includes(args.tab)) {
                    return { success: false, message: `Tab không hợp lệ: '${args.tab}'. Chỉ chấp nhận: photo, video, document.` };
                }

                // Auto-open material panel if not already open
                if (typeof window !== "undefined" && typeof window.openPanel === "function") {
                    window.openPanel("material");
                }

                // Dispatch event to switch tab inside MaterialContent
                if (typeof window !== "undefined") {
                    // Small delay to ensure panel is open before switching tab
                    await new Promise(resolve => setTimeout(resolve, 300));
                    window.dispatchEvent(new CustomEvent("switch_material_tab", { detail: { tab: args.tab } }));
                }

                const tabNames: Record<string, string> = { photo: "Hình ảnh", video: "Video", document: "Tài liệu" };
                return { success: true, message: `Đã chuyển sang tab ${tabNames[args.tab]}.`, instruction: `Thông báo cho người dùng rằng bạn đã chuyển sang tab ${tabNames[args.tab]}.` };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    open_3d_library: {
        declaration: {
            name: "open_3d_library",
            description:
                "Mở Thư viện 3D (Gallery) để xem các mô hình 3D của dự án. Sử dụng khi người dùng yêu cầu xem mô hình 3D, mở thư viện 3D, hoặc nói 'mở thư viện 3D', 'xem 3D', 'open 3D gallery', 'mở gallery'. Nếu Thư viện 3D đã mở sẵn thì KHÔNG cần gọi lại.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.openPanel === "function") {
                    window.openPanel("gallery");
                    return { success: true, message: "Đã mở Thư viện 3D.", instruction: "Thông báo cho người dùng rằng bạn đã mở Thư viện 3D và có thể chọn xem các mô hình 3D." };
                }
                return { success: false, message: "Không thể mở Thư viện 3D - chức năng chưa sẵn sàng." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_3d_library: {
        declaration: {
            name: "close_3d_library",
            description:
                "Đóng Thư viện 3D (Gallery) đang mở. Sử dụng khi người dùng yêu cầu đóng thư viện 3D, tắt gallery, hoặc nói 'đóng thư viện 3D', 'tắt 3D', 'close 3D gallery'. KHÔNG dùng close_tab cho việc này.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.closeDrawerMenu === "function") {
                    window.closeDrawerMenu("gallery");
                    return { success: true, message: "Đã đóng Thư viện 3D.", instruction: "Thông báo cho người dùng rằng bạn đã đóng Thư viện 3D." };
                }
                return { success: false, message: "Không thể đóng Thư viện 3D." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_model: {
        declaration: {
            name: "close_model",
            description:
                "Đóng mô hình 3D (hiện vật 3D / 3D model) đang mở và quay lại danh sách Hiện vật 3D (Thư viện 3D / Gallery). BẮT BUỘC sử dụng khi người dùng yêu cầu: 'đóng model', 'đóng mô hình 3D', 'đóng hiện vật 3D', 'tắt model', 'tắt 3D', 'thoát xem 3D', 'quay lại danh sách hiện vật'. TUYỆT ĐỐI KHÔNG mở tab hình ảnh hay module tư liệu khi người dùng yêu cầu đóng model 3D.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined") {
                    if (typeof (window as any).handleCloseGalleryDetail === 'function') {
                        (window as any).handleCloseGalleryDetail();
                    }
                    window.dispatchEvent(new CustomEvent("close_model"));
                    if (typeof (window as any).openPanel === 'function') {
                        (window as any).openPanel("gallery");
                    }
                    if (typeof window.closeDrawerMenu === 'function') {
                        window.closeDrawerMenu("material");
                    }
                    if (typeof (window as any).silenceAllNonTourAudio === 'function') {
                        (window as any).silenceAllNonTourAudio();
                    }
                }
                return {
                    success: true,
                    message: "Đã đóng mô hình 3D và quay lại danh sách Hiện vật 3D.",
                    instruction: "Thông báo cho người dùng rằng bạn đã đóng mô hình 3D và quay lại danh sách Hiện vật 3D trong Thư viện 3D. KHÔNG mở ảnh hay tư liệu."
                };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    open_material_asset_detail: {
        declaration: {
            name: "open_material_asset_detail",
            description:
                "Mở xem chi tiết một tư liệu cụ thể (hình ảnh, video, hoặc tài liệu) trong module Tư liệu. Sử dụng khi người dùng yêu cầu xem chi tiết một asset, ví dụ: 'mở ảnh đó xem', 'xem video chi tiết', 'mở tài liệu đó'. Cần có uuid của asset. Nếu module Tư liệu chưa mở, sẽ tự động mở trước.",
            parameters: {
                type: "OBJECT",
                properties: {
                    asset_uuid: {
                        type: "STRING",
                        description: "UUID duy nhất của asset cần xem chi tiết.",
                    },
                    asset_type: {
                        type: "STRING",
                        description: "Loại asset: 'photo' (hình ảnh), 'video' (video), 'document' (tài liệu).",
                        enum: ["photo", "video", "document"],
                    },
                    category_uuid: {
                        type: "STRING",
                        description: "UUID của danh mục chứa asset (tùy chọn, giúp hiển thị chính xác hơn).",
                    },
                },
                required: ["asset_uuid", "asset_type"],
            },
        },
        handler: async (args: any) => {
            try {
                // Auto-open material panel if not already open
                if (typeof window !== "undefined" && typeof window.openPanel === "function") {
                    window.openPanel("material");
                }

                // Small delay to ensure panel is mounted
                await new Promise(resolve => setTimeout(resolve, 500));

                if (typeof window !== "undefined" && typeof window.handleOpenMaterialDetail === "function") {
                    window.handleOpenMaterialDetail(args.asset_uuid, args.asset_type, args.category_uuid || "");
                    const typeNames: Record<string, string> = { photo: "hình ảnh", video: "video", document: "tài liệu" };
                    return { success: true, message: `Đã mở chi tiết ${typeNames[args.asset_type] || args.asset_type}.`, instruction: `Thông báo cho người dùng rằng bạn đã mở chi tiết ${typeNames[args.asset_type] || args.asset_type}.` };
                }
                return { success: false, message: "Không thể mở chi tiết tư liệu - chức năng chưa sẵn sàng." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_material_asset_detail: {
        declaration: {
            name: "close_material_asset_detail",
            description:
                "Đóng cửa sổ xem chi tiết tư liệu (hình ảnh, video, tài liệu) đang mở trong module Tư liệu. CHỈ sử dụng cho hình ảnh, video, tài liệu trong module Tư liệu. KHÔNG DÙNG CHO MÔ HÌNH 3D / HIỆN VẬT 3D (với 3D model, BẮT BUỘC dùng close_model). Sau khi đóng sẽ quay lại danh sách tư liệu.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("close_tab"));
                    return { success: true, message: "Đã đóng chi tiết tư liệu và quay lại danh sách.", instruction: "Thông báo cho người dùng rằng bạn đã đóng chi tiết và quay lại danh sách tư liệu." };
                }
                return { success: false, message: "Không thể đóng chi tiết tư liệu." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    // ==================== LANGUAGE CONTROLS ====================
    get_available_languages: {
        declaration: {
            name: "get_available_languages",
            description:
                "Lấy danh sách ngôn ngữ có sẵn của dự án hiện tại. Sử dụng khi người dùng hỏi dự án có mấy ngôn ngữ, ngôn ngữ nào có sẵn, hoặc trước khi chuyển ngôn ngữ để kiểm tra. Trả về danh sách ngôn ngữ với id và tên.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            try {
                const languages = (window as any).languageList;
                if (Array.isArray(languages) && languages.length > 0) {
                    const currentLang = typeof window.getCurrentLanguage === 'function' ? window.getCurrentLanguage() : null;
                    const langList = languages.map((lang: any) => ({
                        id: lang.id,
                        name: lang.name || lang.id,
                    }));
                    return {
                        success: true,
                        current_language: currentLang ? { id: currentLang.id, name: currentLang.name } : null,
                        available_languages: langList,
                        message: `Dự án có ${langList.length} ngôn ngữ: ${langList.map((l: any) => l.name).join(', ')}. Ngôn ngữ hiện tại: ${currentLang?.name || 'không xác định'}.`,
                        instruction: "Thông báo cho người dùng danh sách ngôn ngữ có sẵn và ngôn ngữ hiện tại của dự án.",
                    };
                }
                return { success: false, message: "Không tìm thấy danh sách ngôn ngữ. Dự án có thể chỉ có 1 ngôn ngữ.", instruction: "Thông báo cho người dùng rằng dự án có thể chỉ có 1 ngôn ngữ." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    switch_language: {
        declaration: {
            name: "switch_language",
            description:
                "Chuyển đổi ngôn ngữ dự án. Sử dụng khi người dùng yêu cầu chuyển ngôn ngữ, ví dụ: 'chuyển sang tiếng Anh', 'switch to English', 'chuyển lại tiếng Việt', 'đổi ngôn ngữ sang tiếng Nhật'. TRƯỚC KHI chuyển, nên gọi get_available_languages để kiểm tra ngôn ngữ nào có sẵn. Mapping phổ biến: tiếng Việt → 'vn', tiếng Anh → 'en', tiếng Nhật → 'ja', tiếng Trung → 'zh', tiếng Hàn → 'ko', tiếng Pháp → 'fr', tiếng Đức → 'de', tiếng Tây Ban Nha → 'es'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    language_id: {
                        type: "STRING",
                        description: "Mã ngôn ngữ cần chuyển (VD: 'en', 'vn', 'ja', 'zh', 'ko', 'fr', 'de', 'es'). Dùng get_available_languages để lấy danh sách mã hợp lệ.",
                    },
                },
                required: ["language_id"],
            },
        },
        handler: async (args: any) => {
            try {
                const langId = args.language_id?.toLowerCase?.() || args.language_id;

                // Validate language exists in project
                const languages = (window as any).languageList;
                if (Array.isArray(languages)) {
                    const found = languages.find((lang: any) => lang.id === langId);
                    if (!found) {
                        const available = languages.map((l: any) => `${l.name} (${l.id})`).join(', ');
                        return { success: false, message: `Ngôn ngữ '${langId}' không có trong dự án. Các ngôn ngữ có sẵn: ${available}.`, instruction: "Thông báo cho người dùng rằng ngôn ngữ họ yêu cầu không có trong dự án, và gợi ý các ngôn ngữ có sẵn." };
                    }
                }

                // Check current language
                const currentLang = typeof window.getCurrentLanguage === 'function' ? window.getCurrentLanguage() : null;
                if (currentLang?.id === langId) {
                    return { success: true, message: `Dự án đã đang ở ngôn ngữ ${currentLang.name} rồi.`, instruction: `Thông báo cho người dùng rằng dự án đã đang ở ngôn ngữ ${currentLang.name} rồi, không cần chuyển.` };
                }

                // Switch language
                if (typeof (window as any).changeLocale === 'function') {
                    (window as any).changeLocale(langId);
                    const langName = languages?.find((l: any) => l.id === langId)?.name || langId;
                    return { success: true, message: `Đã chuyển ngôn ngữ dự án sang ${langName} thành công.`, instruction: `BẮT BUỘC phải xác nhận với người dùng rằng bạn đã chuyển ngôn ngữ dự án sang ${langName} thành công. Phản hồi bằng ngôn ngữ mới nếu có thể.` };
                }
                return { success: false, message: "Không thể chuyển ngôn ngữ - chức năng chưa sẵn sàng.", instruction: "Thông báo cho người dùng rằng chức năng chuyển ngôn ngữ chưa sẵn sàng." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    // ==================== PANEL CONTROLS (SIDEBAR) ====================
    open_introduction: {
        declaration: {
            name: "open_introduction",
            description:
                "Mở panel Giới thiệu dự án. Sử dụng khi người dùng yêu cầu xem giới thiệu, thông tin dự án, hoặc nói 'mở giới thiệu', 'giới thiệu dự án', 'open introduction', 'about this project'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.openPanel === "function") {
                    window.openPanel("introduction");
                    return { success: true, message: "Đã mở panel Giới thiệu dự án.", instruction: "Thông báo cho người dùng rằng bạn đã mở phần Giới thiệu dự án để họ xem." };
                }
                return { success: false, message: "Không thể mở Giới thiệu - chức năng chưa sẵn sàng." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_introduction: {
        declaration: {
            name: "close_introduction",
            description:
                "Đóng panel Giới thiệu dự án đang mở. Sử dụng khi người dùng yêu cầu đóng giới thiệu, tắt giới thiệu, hoặc nói 'đóng giới thiệu', 'tắt giới thiệu', 'close introduction'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.closeDrawerMenu === "function") {
                    window.closeDrawerMenu("introduction");
                    return { success: true, message: "Đã đóng panel Giới thiệu.", instruction: "Thông báo cho người dùng rằng bạn đã đóng phần Giới thiệu." };
                }
                return { success: false, message: "Không thể đóng Giới thiệu." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    open_minimap: {
        declaration: {
            name: "open_minimap",
            description:
                "Mở bản đồ nội khu (Minimap) dạng popup. Sử dụng khi người dùng yêu cầu xem bản đồ nội khu, minimap, bản đồ dự án, sơ đồ mặt bằng, hoặc nói 'mở bản đồ', 'xem bản đồ nội khu', 'open minimap', 'show map'. Lưu ý: Đây là bản đồ nội khu dự án, KHÔNG phải Google Map (dùng open_google_map cho Google Map).",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("toggle_qtsc_minimap", { detail: { show: true } }));
                    return { success: true, message: "Đã mở bản đồ nội khu (Minimap).", instruction: "Thông báo cho người dùng rằng bạn đã mở bản đồ nội khu để họ xem sơ đồ mặt bằng." };
                }
                return { success: false, message: "Không thể mở Minimap." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_minimap: {
        declaration: {
            name: "close_minimap",
            description:
                "Đóng bản đồ nội khu (Minimap) đang mở. Sử dụng khi người dùng yêu cầu đóng bản đồ, tắt minimap, hoặc nói 'đóng bản đồ', 'tắt bản đồ nội khu', 'close minimap'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new CustomEvent("toggle_qtsc_minimap", { detail: { show: false } }));
                    return { success: true, message: "Đã đóng bản đồ nội khu (Minimap).", instruction: "Thông báo cho người dùng rằng bạn đã đóng bản đồ nội khu." };
                }
                return { success: false, message: "Không thể đóng Minimap." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    open_contact: {
        declaration: {
            name: "open_contact",
            description:
                "Mở panel Liên hệ. Sử dụng khi người dùng yêu cầu liên hệ, thông tin liên lạc, hoặc nói 'mở liên hệ', 'liên hệ', 'contact', 'open contact'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.openPanel === "function") {
                    window.openPanel("contact");
                    return { success: true, message: "Đã mở panel Liên hệ.", instruction: "Thông báo cho người dùng rằng bạn đã mở phần Liên hệ." };
                }
                return { success: false, message: "Không thể mở Liên hệ." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_contact: {
        declaration: {
            name: "close_contact",
            description:
                "Đóng panel Liên hệ đang mở. Sử dụng khi người dùng yêu cầu đóng liên hệ, tắt liên hệ, hoặc nói 'đóng liên hệ', 'close contact'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.closeDrawerMenu === "function") {
                    window.closeDrawerMenu("contact");
                    return { success: true, message: "Đã đóng panel Liên hệ.", instruction: "Thông báo cho người dùng rằng bạn đã đóng phần Liên hệ." };
                }
                return { success: false, message: "Không thể đóng Liên hệ." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    open_settings: {
        declaration: {
            name: "open_settings",
            description:
                "Mở panel Cài đặt. Sử dụng khi người dùng yêu cầu mở cài đặt, thiết lập, hoặc nói 'mở cài đặt', 'cài đặt', 'settings', 'open settings'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.openPanel === "function") {
                    window.openPanel("settings");
                    return { success: true, message: "Đã mở panel Cài đặt.", instruction: "Thông báo cho người dùng rằng bạn đã mở phần Cài đặt." };
                }
                return { success: false, message: "Không thể mở Cài đặt." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_settings: {
        declaration: {
            name: "close_settings",
            description:
                "Đóng panel Cài đặt đang mở. Sử dụng khi người dùng yêu cầu đóng cài đặt, tắt cài đặt, hoặc nói 'đóng cài đặt', 'close settings'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.closeDrawerMenu === "function") {
                    window.closeDrawerMenu("settings");
                    return { success: true, message: "Đã đóng panel Cài đặt.", instruction: "Thông báo cho người dùng rằng bạn đã đóng phần Cài đặt." };
                }
                return { success: false, message: "Không thể đóng Cài đặt." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    // ==================== SETTINGS & DISPLAY CONTROLS ====================
    set_fullscreen: {
        declaration: {
            name: "set_fullscreen",
            description:
                "Bật chế độ toàn màn hình hoặc chuyển sang chế độ màn hình nhỏ (thu nhỏ / thoát toàn màn hình). Sử dụng khi người dùng yêu cầu 'toàn màn hình', 'bật toàn màn hình', 'phóng to màn hình', hoặc 'màn hình nhỏ', 'chuyển sang màn hình nhỏ', 'thu nhỏ màn hình', 'thoát toàn màn hình', 'fullscreen', 'exit fullscreen'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    fullscreen: {
                        type: "BOOLEAN",
                        description: "true = bật toàn màn hình; false = chuyển sang chế độ màn hình nhỏ (thu nhỏ / thoát toàn màn hình).",
                    },
                },
                required: ["fullscreen"],
            },
        },
        handler: async (args: any) => {
            try {
                const fullscreen = typeof args?.fullscreen === "boolean" ? args.fullscreen : true;
                if (typeof window !== "undefined" && typeof (window as any).toggleFullScreen === "function") {
                    (window as any).toggleFullScreen(fullscreen);
                    const stateMsg = fullscreen ? "toàn màn hình" : "màn hình nhỏ";
                    return {
                        success: true,
                        message: `Đã chuyển sang chế độ ${stateMsg}.`,
                        instruction: `Xác nhận rõ ràng với người dùng rằng bạn đã chuyển sang chế độ ${stateMsg}.`,
                    };
                }
                return { success: false, message: "Không thể điều khiển chế độ toàn màn hình." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    set_auto_rotate: {
        declaration: {
            name: "set_auto_rotate",
            description:
                "Bật hoặc tắt chế độ tự động xoay không gian 360 độ trong phần Cài đặt. Sử dụng khi người dùng yêu cầu 'tự động xoay', 'bật tự động xoay', 'xoay tự động', 'dừng xoay', 'tắt tự động xoay', 'dừng tự động xoay'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    enabled: {
                        type: "BOOLEAN",
                        description: "true = bật tự động xoay 360; false = tắt / dừng tự động xoay.",
                    },
                },
                required: ["enabled"],
            },
        },
        handler: async (args: any) => {
            try {
                const enabled = typeof args?.enabled === "boolean" ? args.enabled : true;
                if (typeof window !== "undefined" && typeof (window as any).setAutoRotate === "function") {
                    (window as any).setAutoRotate(enabled);
                    const stateMsg = enabled ? "bật" : "tắt";
                    return {
                        success: true,
                        message: `Đã ${stateMsg} chế độ tự động xoay 360 độ.`,
                        instruction: `Thông báo cho người dùng rằng bạn đã ${stateMsg} tự động xoay không gian 360 độ.`,
                    };
                }
                return { success: false, message: "Không thể điều khiển chế độ tự động xoay." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    set_3d_navigation: {
        declaration: {
            name: "set_3d_navigation",
            description:
                "Bật hoặc tắt hiển thị các điểm điều hướng 3D (hotspots / điểm tương tác di chuyển giữa các không gian) trong Cài đặt. Sử dụng khi người dùng yêu cầu 'bật điều hướng 3D', 'ẩn điều hướng 3D', 'hiển thị các điểm điều hướng', 'tắt hotspot', 'bật hotspot', 'ẩn điểm di chuyển'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    enabled: {
                        type: "BOOLEAN",
                        description: "true = hiển thị điểm điều hướng 3D (hotspot); false = ẩn điểm điều hướng 3D.",
                    },
                },
                required: ["enabled"],
            },
        },
        handler: async (args: any) => {
            try {
                const enabled = typeof args?.enabled === "boolean" ? args.enabled : true;
                if (typeof window !== "undefined" && typeof (window as any).set3DNavigation === "function") {
                    (window as any).set3DNavigation(enabled);
                    const stateMsg = enabled ? "bật hiển thị" : "ẩn";
                    return {
                        success: true,
                        message: `Đã ${stateMsg} các điểm điều hướng 3D.`,
                        instruction: `Thông báo cho người dùng rằng bạn đã ${stateMsg} điểm điều hướng 3D.`,
                    };
                }
                return { success: false, message: "Không thể điều khiển điều hướng 3D." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    set_background_music: {
        declaration: {
            name: "set_background_music",
            description:
                "Bật hoặc tắt riêng nhạc nền của dự án trong phần Cài đặt. Sử dụng khi người dùng yêu cầu 'bật nhạc nền', 'tắt nhạc nền', 'dừng nhạc nền', 'phát nhạc nền'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    enabled: {
                        type: "BOOLEAN",
                        description: "true = bật nhạc nền; false = tắt nhạc nền.",
                    },
                },
                required: ["enabled"],
            },
        },
        handler: async (args: any) => {
            try {
                const enabled = typeof args?.enabled === "boolean" ? args.enabled : true;
                if (typeof window !== "undefined" && typeof (window as any).setBackgroundMusicEnabled === "function") {
                    (window as any).setBackgroundMusicEnabled(enabled);
                    const stateMsg = enabled ? "bật" : "tắt";
                    return {
                        success: true,
                        message: `Đã ${stateMsg} nhạc nền.`,
                        instruction: `Thông báo cho người dùng rằng bạn đã ${stateMsg} nhạc nền của dự án.`,
                    };
                }
                return { success: false, message: "Không thể điều khiển nhạc nền." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    set_narration: {
        declaration: {
            name: "set_narration",
            description:
                "Bật hoặc tắt giọng đọc thuyết minh tự động của dự án trong phần Cài đặt. Sử dụng khi người dùng yêu cầu 'bật thuyết minh', 'tắt thuyết minh', 'dừng giọng đọc', 'bật giọng đọc giới thiệu không gian'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    enabled: {
                        type: "BOOLEAN",
                        description: "true = bật thuyết minh tự động; false = tắt thuyết minh tự động.",
                    },
                },
                required: ["enabled"],
            },
        },
        handler: async (args: any) => {
            try {
                const enabled = typeof args?.enabled === "boolean" ? args.enabled : true;
                if (typeof window !== "undefined" && typeof (window as any).setNarrationEnabled === "function") {
                    (window as any).setNarrationEnabled(enabled);
                    const stateMsg = enabled ? "bật" : "tắt";
                    return {
                        success: true,
                        message: `Đã ${stateMsg} giọng đọc thuyết minh.`,
                        instruction: `Thông báo cho người dùng rằng bạn đã ${stateMsg} thuyết minh tự động.`,
                    };
                }
                return { success: false, message: "Không thể điều khiển thuyết minh." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    set_view_mode: {
        declaration: {
            name: "set_view_mode",
            description:
                "Chuyển đổi chế độ góc nhìn / hiển thị không gian 360 trong Cài đặt. Hỗ trợ các chế độ: 'normal' (bình thường), 'planet' (hành tinh nhỏ / little planet), 'wide' (góc nhìn rộng / pannini), 'vr' (thực tế ảo VR), 'fisheye' (mắt cá), 'stereographic', 'architectural' (kiến trúc). Sử dụng khi người dùng nói 'chế độ hành tinh nhỏ', 'góc nhìn rộng', 'chế độ bình thường', 'góc nhìn bình thường', 'bật chế độ VR', 'thoát góc nhìn hành tinh'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    mode: {
                        type: "STRING",
                        description: "Chế độ góc nhìn mong muốn: 'normal' (bình thường), 'planet' (hành tinh nhỏ), 'wide' (góc nhìn rộng), 'vr' (thực tế ảo VR), 'fisheye' (mắt cá), 'stereographic', 'architectural'.",
                    },
                },
                required: ["mode"],
            },
        },
        handler: async (args: any) => {
            try {
                const mode = String(args?.mode || "normal").toLowerCase();
                if (typeof window !== "undefined" && typeof (window as any).setViewMode === "function") {
                    (window as any).setViewMode(mode);
                    const modeNames: Record<string, string> = {
                        normal: "Bình thường",
                        planet: "Hành tinh nhỏ",
                        wide: "Góc nhìn rộng (Pannini)",
                        vr: "Thực tế ảo VR",
                        fisheye: "Mắt cá (Fisheye)",
                        stereographic: "Stereographic",
                        architectural: "Kiến trúc",
                    };
                    const modeName = modeNames[mode] || mode;
                    return {
                        success: true,
                        message: `Đã chuyển sang chế độ góc nhìn ${modeName}.`,
                        instruction: `Thông báo cho người dùng rằng bạn đã chuyển sang chế độ góc nhìn ${modeName}.`,
                    };
                }
                return { success: false, message: "Không thể thay đổi chế độ góc nhìn." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    open_guide: {
        declaration: {
            name: "open_guide",
            description:
                "Mở popup hướng dẫn sử dụng giao diện và thao tác tương tác 3D trong Cài đặt. Sử dụng khi người dùng yêu cầu 'hướng dẫn', 'mở hướng dẫn', 'xem hướng dẫn giao diện', 'chỉ cách thao tác'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof (window as any).openGuide === "function") {
                    (window as any).openGuide();
                    return { success: true, message: "Đã mở hướng dẫn sử dụng giao diện.", instruction: "Thông báo cho người dùng rằng bạn đã mở bảng hướng dẫn sử dụng giao diện." };
                }
                return { success: false, message: "Không thể mở hướng dẫn." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_guide: {
        declaration: {
            name: "close_guide",
            description:
                "Đóng cửa sổ hướng dẫn sử dụng giao diện. Sử dụng khi người dùng yêu cầu 'đóng hướng dẫn', 'tắt hướng dẫn'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof (window as any).closeGuide === "function") {
                    (window as any).closeGuide();
                    return { success: true, message: "Đã đóng hướng dẫn sử dụng.", instruction: "Thông báo cho người dùng rằng bạn đã đóng bảng hướng dẫn." };
                }
                return { success: false, message: "Không thể đóng hướng dẫn." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    open_autotour_panel: {
        declaration: {
            name: "open_autotour_panel",
            description:
                "Mở panel danh sách AutoTour trên giao diện UI. Sử dụng khi người dùng yêu cầu XEM danh sách tour trên màn hình, hoặc nói 'mở panel tour', 'mở danh sách autotour trên màn hình'. Đây chỉ mở panel DANH SÁCH trên UI, KHÔNG bắt đầu phát tour. Để PHÁT autotour có sẵn (với voice đã tạo), dùng start_existing_autotour. Để TẠO tour mới (agent tự thuyết minh), dùng create_virtual_tour.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.openPanel === "function") {
                    window.openPanel("autoTour");
                    return { success: true, message: "Đã mở panel danh sách AutoTour.", instruction: "Thông báo cho người dùng rằng bạn đã mở danh sách các tour tự động có sẵn." };
                }
                return { success: false, message: "Không thể mở AutoTour." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_autotour_panel: {
        declaration: {
            name: "close_autotour_panel",
            description:
                "Đóng panel danh sách AutoTour đang mở. Sử dụng khi người dùng yêu cầu đóng tour, tắt autotour, hoặc nói 'đóng tour', 'đóng autotour', 'close autotour'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.closeDrawerMenu === "function") {
                    window.closeDrawerMenu("autoTour");
                    return { success: true, message: "Đã đóng panel AutoTour.", instruction: "Thông báo cho người dùng rằng bạn đã đóng danh sách tour." };
                }
                return { success: false, message: "Không thể đóng AutoTour." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    open_blog: {
        declaration: {
            name: "open_blog",
            description:
                "Mở panel Blog / Bài viết. Sử dụng khi người dùng yêu cầu xem blog, bài viết, tin tức, hoặc nói 'mở blog', 'xem bài viết', 'open blog', 'show articles'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.openPanel === "function") {
                    window.openPanel("blog");
                    return { success: true, message: "Đã mở panel Blog / Bài viết.", instruction: "Thông báo cho người dùng rằng bạn đã mở phần Blog / Bài viết." };
                }
                return { success: false, message: "Không thể mở Blog." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_blog: {
        declaration: {
            name: "close_blog",
            description:
                "Đóng panel Blog / Bài viết đang mở. Sử dụng khi người dùng yêu cầu đóng blog, tắt bài viết, hoặc nói 'đóng blog', 'close blog'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.closeDrawerMenu === "function") {
                    window.closeDrawerMenu("blog");
                    return { success: true, message: "Đã đóng panel Blog.", instruction: "Thông báo cho người dùng rằng bạn đã đóng phần Blog." };
                }
                return { success: false, message: "Không thể đóng Blog." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    open_group_detail: {
        declaration: {
            name: "open_group_detail",
            description:
                "Mở trang chi tiết một nhóm cảnh / khu vực tham quan cụ thể trên panel 'Khám phá thông tin' (hiển thị ảnh bìa, phần giới thiệu và danh sách cảnh con như Hình 4). BẮT BUỘC SỬ DỤNG khi người dùng đang ở tab Khám phá thông tin hoặc yêu cầu: 'mở nhóm [tên nhóm]', 'xem nhóm [tên nhóm]', 'mở một nhóm bất kỳ', 'chuyển tới nhóm [tên nhóm]', 'xem thông tin nhóm [tên nhóm]' (ví dụ: 'mở nhóm Nhà lưu niệm', 'mở một nhóm bất kỳ', 'xem nhóm không gian bên ngoài').",
            parameters: {
                type: "OBJECT",
                properties: {
                    group_name: {
                        type: "STRING",
                        description: "Tên nhóm cần mở (ví dụ: 'Nhà lưu niệm', 'Không gian bên ngoài Bảo tàng', hoặc 'bất kỳ' / 'ngẫu nhiên' nếu người dùng nói mở một nhóm bất kỳ)."
                    }
                },
                required: ["group_name"]
            }
        },
        handler: async (args: any) => {
            try {
                if (typeof window === "undefined") {
                    return { success: false, message: "Window không khả dụng" };
                }

                const groupQuery = (args?.group_name || "").trim();

                // 1. Ưu tiên cao nhất: Gọi trực tiếp openGroupDetail trên window đã kết nối GroupScene state
                if (typeof (window as any).openGroupDetail === 'function') {
                    const result = (window as any).openGroupDetail(groupQuery || "bất kỳ");
                    if (result?.success || result === true) {
                        const targetTitle = result?.title || groupQuery || "nhóm cảnh";
                        return {
                            success: true,
                            message: `Đã mở chi tiết nhóm "${targetTitle}".`,
                            instruction: `Thông báo cho người dùng rằng bạn đã mở trang chi tiết của nhóm "${targetTitle}" với ảnh bìa, phần giới thiệu và danh sách cảnh tham quan.`
                        };
                    }
                }

                // 2. Mở panel nếu chưa mở
                if (typeof (window as any).openPanel === 'function') {
                    (window as any).openPanel("groupView");
                } else if (typeof window.handleOpenGroupView === 'function') {
                    window.handleOpenGroupView();
                }

                // Chờ ngắn để GroupView gắn hook
                await new Promise(r => setTimeout(r, 200));
                if (typeof (window as any).openGroupDetail === 'function') {
                    const result = (window as any).openGroupDetail(groupQuery || "bất kỳ");
                    if (result?.success || result === true) {
                        const targetTitle = result?.title || groupQuery || "nhóm cảnh";
                        return {
                            success: true,
                            message: `Đã mở chi tiết nhóm "${targetTitle}".`,
                            instruction: `Thông báo cho người dùng rằng bạn đã mở trang chi tiết của nhóm "${targetTitle}".`
                        };
                    }
                }

                // 3. Fallback: Truy vấn API có hỗ trợ sublink
                const idPath = window.id_path || (window as any).idPath || "";
                const lang = getLanguageId();
                const sublink = (window as any).sublink || "1";

                let groups: any[] = [];
                if (sublink !== "1") {
                    const { default: sublinkServices } = await import('@/templates/Heritage/utils/services/sublink');
                    const sublinkRes: any = await sublinkServices.getParentGroupBySublink({
                        id_path: idPath,
                        language: lang,
                        page: '1',
                        limit: '50',
                        version_id: '1',
                        sublink: sublink,
                        show_children_groups: 'true'
                    });
                    groups = Array.isArray(sublinkRes?.data) ? sublinkRes.data : [];
                }

                if (!groups.length) {
                    const { default: groupViewServices } = await import('@/templates/Heritage/utils/services/groupView');
                    const response: any = await groupViewServices.getAllParentGroup({
                        id_path: idPath,
                        language: lang,
                        page: '1',
                        limit: '50',
                        version_id: '1',
                        is_show: '1'
                    });
                    groups = Array.isArray(response?.data) ? response.data : [];
                }

                if (!groups.length) {
                    return { success: false, message: "Không tìm thấy dữ liệu nhóm cảnh của dự án." };
                }

                const isRandom = !groupQuery || groupQuery.toLowerCase().includes("bất kỳ") || groupQuery.toLowerCase().includes("ngẫu nhiên") || groupQuery.toLowerCase().includes("random");
                let targetGroup: any = null;
                if (isRandom) {
                    targetGroup = groups[0];
                } else {
                    const lowerQ = groupQuery.toLowerCase();
                    targetGroup = groups.find((g: any) => {
                        const title = (g.title || g.name || "").toLowerCase();
                        return title.includes(lowerQ) || lowerQ.includes(title);
                    }) || groups[0];
                }

                if (targetGroup && typeof (window as any).openGroupDetail === 'function') {
                    (window as any).openGroupDetail(targetGroup);
                    return {
                        success: true,
                        message: `Đã mở chi tiết nhóm "${targetGroup.title || targetGroup.name}".`,
                        instruction: `Thông báo cho người dùng rằng bạn đã mở trang chi tiết của nhóm "${targetGroup.title || targetGroup.name}".`
                    };
                }

                return { success: false, message: "Không tìm thấy nhóm phù hợp." };
            } catch (error: any) {
                console.error("❌ Error in open_group_detail:", error);
                return { success: false, error: error.message };
            }
        }
    },

    open_group_view: {
        declaration: {
            name: "open_group_view",
            description:
                "Mở tab Khám phá thông tin / Nhóm cảnh / Điểm tham quan trên sidebar. Nếu có truyền group_name, sẽ mở trực tiếp trang chi tiết của nhóm đó. Nếu không có group_name, sẽ mở danh sách các nhóm cảnh. Sử dụng khi người dùng nói: 'mở khám phá thông tin', 'mở điểm tham quan', 'xem nhóm cảnh', 'khám phá', 'open explore', 'show groups', 'show scenic spots'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    group_name: {
                        type: "STRING",
                        description: "Tên nhóm cần mở chi tiết (ví dụ: 'Nhà lưu niệm', hoặc 'bất kỳ'). Nếu để trống sẽ mở danh sách tổng hợp."
                    }
                }
            },
        },
        handler: async (args?: any) => {
            try {
                if (args?.group_name && typeof args.group_name === 'string' && args.group_name.trim() !== '') {
                    return tools.open_group_detail.handler(args);
                }
                if (typeof window !== "undefined") {
                    if (typeof (window as any).openPanel === "function") {
                        (window as any).openPanel("groupView");
                    } else if (typeof window.handleOpenGroupView === "function") {
                        window.handleOpenGroupView();
                    }
                    return { success: true, message: "Đã mở panel Nhóm cảnh / Điểm tham quan.", instruction: "Thông báo cho người dùng rằng bạn đã mở danh sách các điểm tham quan." };
                }
                return { success: false, message: "Không thể mở Nhóm cảnh." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_group_view: {
        declaration: {
            name: "close_group_view",
            description:
                "Đóng tab Khám phá thông tin / Nhóm cảnh / Điểm tham quan. Sử dụng khi người dùng nói: 'đóng khám phá thông tin', 'tắt nhóm cảnh', 'đóng điểm tham quan', 'close explore', 'close groups'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.closeDrawerMenu === "function") {
                    window.closeDrawerMenu("groupView");
                    return { success: true, message: "Đã đóng panel Nhóm cảnh.", instruction: "Thông báo cho người dùng rằng bạn đã đóng danh sách điểm tham quan." };
                }
                return { success: false, message: "Không thể đóng Nhóm cảnh." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    open_ar_navigation: {
        declaration: {
            name: "open_ar_navigation",
            description:
                "Mở AR Navigation (Dẫn đường thực tế tăng cường). Sử dụng khi người dùng yêu cầu mở AR, dẫn đường AR, hoặc nói 'mở AR', 'AR navigation', 'open AR', 'dẫn đường'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.openPanel === "function") {
                    window.openPanel("arNavigation");
                    return { success: true, message: "Đã mở AR Navigation.", instruction: "Thông báo cho người dùng rằng bạn đã mở chức năng dẫn đường thực tế tăng cường (AR)." };
                }
                return { success: false, message: "Không thể mở AR Navigation." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_ar_navigation: {
        declaration: {
            name: "close_ar_navigation",
            description:
                "Đóng AR Navigation đang mở. Sử dụng khi người dùng yêu cầu đóng AR, tắt dẫn đường, hoặc nói 'đóng AR', 'close AR', 'tắt AR navigation'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.closeDrawerMenu === "function") {
                    window.closeDrawerMenu("arNavigation");
                    return { success: true, message: "Đã đóng AR Navigation.", instruction: "Thông báo cho người dùng rằng bạn đã đóng chức năng AR." };
                }
                return { success: false, message: "Không thể đóng AR Navigation." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    // ==================== GOOGLE MAP / CESIUM MAP ====================
    open_google_map: {
        declaration: {
            name: "open_google_map",
            description:
                "Mở bản đồ Google Map từ menu. Sử dụng khi người dùng yêu cầu mở Google Map, bản đồ từ menu, hoặc nói 'mở google map', 'open google map'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.openPanel === "function") {
                    window.openPanel("minimap");
                    return { success: true, message: "Đã mở Google Map.", instruction: "Thông báo cho người dùng rằng bạn đã mở Google Map." };
                }
                return { success: false, message: "Không thể mở Google Map." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_google_map: {
        declaration: {
            name: "close_google_map",
            description:
                "Đóng Google Map đang mở. Sử dụng khi người dùng yêu cầu đóng Google Map, hoặc nói 'đóng google map', 'close google map'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof window.closeDrawerMenu === "function") {
                    window.closeDrawerMenu("minimap");
                    return { success: true, message: "Đã đóng Google Map.", instruction: "Thông báo cho người dùng rằng bạn đã đóng Google Map." };
                }
                return { success: false, message: "Không thể đóng Google Map." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    open_cesium_map: {
        declaration: {
            name: "open_cesium_map",
            description:
                "Mở bản đồ Cesium Map 3D. Sử dụng khi người dùng yêu cầu mở Cesium Map, bản đồ 3D Cesium, hoặc nói 'mở cesium map', 'mở bản đồ cesium'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof (window as any).openCesiumMap === "function") {
                    (window as any).openCesiumMap();
                    return { success: true, message: "Đã mở Cesium Map.", instruction: "Thông báo cho người dùng rằng bạn đã mở bản đồ Cesium 3D." };
                }
                return { success: false, message: "Không thể mở Cesium Map - chức năng chưa sẵn sàng." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_cesium_map: {
        declaration: {
            name: "close_cesium_map",
            description:
                "Đóng Cesium Map 3D đang mở. Sử dụng khi người dùng yêu cầu đóng Cesium Map, tắt bản đồ 3D Cesium, hoặc nói 'đóng cesium map'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined" && typeof (window as any).closeCesiumMap === "function") {
                    (window as any).closeCesiumMap();
                    return { success: true, message: "Đã đóng Cesium Map.", instruction: "Thông báo cho người dùng rằng bạn đã đóng bản đồ Cesium." };
                }
                return { success: false, message: "Không thể đóng Cesium Map." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    // ==================== AUDIO CONTROLS ====================
    toggle_audio: {
        declaration: {
            name: "toggle_audio",
            description:
                "Bật hoặc tắt âm thanh (mute/unmute). Sử dụng khi người dùng yêu cầu tắt âm thanh, bật âm thanh, hoặc nói 'tắt tiếng', 'mute', 'bật âm thanh', 'unmute', 'tắt nhạc', 'bật nhạc lại', 'phát âm thanh', 'bật thuyết minh'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    mute: {
                        type: "BOOLEAN",
                        description: "true = tắt âm thanh (mute), false = bật âm thanh (unmute).",
                    },
                },
                required: ["mute"],
            },
        },
        handler: async (args: any) => {
            try {
                if (typeof window !== "undefined") {
                    const shouldMute = Boolean(args.mute);
                    if (shouldMute) {
                        (window as any).__aiAgentExplicitAudioEnabled = false;
                        if (typeof window.toggleMute === "function") {
                            window.toggleMute(true);
                        }
                        if (typeof (window as any).silenceAllNonTourAudio === "function") {
                            (window as any).silenceAllNonTourAudio();
                        }
                    } else {
                        (window as any).__aiAgentExplicitAudioEnabled = true;
                        if (typeof window.toggleMute === "function") {
                            window.toggleMute(false);
                        }
                        if (typeof window.playCurrentSceneAudio === "function") {
                            window.playCurrentSceneAudio(true);
                        }
                    }
                    const stateMsg = shouldMute ? "tắt" : "bật";
                    return {
                        success: true,
                        message: `Đã ${stateMsg} âm thanh theo cấu hình của cảnh.`,
                        instruction: `Thông báo cho người dùng rằng bạn đã ${stateMsg} âm thanh.`
                    };
                }
                return { success: false, message: "Không thể điều khiển âm thanh." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    // ==================== SIDEBAR MENU CONTROLS ====================
    open_sidebar_menu: {
        declaration: {
            name: "open_sidebar_menu",
            description:
                "Mở thanh menu chính (menu bên trái chứa các mục: Giới thiệu, Khám phá thông tin, Tour tự động, Bản đồ, Tư liệu, Cài đặt, Liên hệ). Sử dụng khi người dùng yêu cầu mở menu, xem menu, hoặc nói 'mở menu', 'mở thanh menu', 'show menu', 'open sidebar', 'bật menu'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined") {
                    if (typeof (window as any).openSidebarMenu === "function") {
                        (window as any).openSidebarMenu();
                        return { success: true, message: "Đã mở thanh menu.", instruction: "Thông báo cho người dùng rằng bạn đã mở thanh menu." };
                    }
                }
                return { success: false, message: "Không thể mở thanh menu." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_sidebar_menu: {
        declaration: {
            name: "close_sidebar_menu",
            description:
                "Đóng hoặc tắt thanh menu chính bên trái đang mở (thanh menu hiển thị Giới thiệu, Khám phá thông tin, Tour tự động, Bản đồ, Tư liệu, Cài đặt, Liên hệ). BẮT BUỘC SỬ DỤNG khi người dùng yêu cầu 'đóng menu', 'tắt menu', 'đóng thanh menu', 'tắt thanh menu', 'tắt menu này đi', 'đóng menu này lại', 'ẩn menu', 'close menu', 'hide sidebar', hoặc khi đang mở menu mà người dùng nói 'tắt đi', 'đóng lại', 'thoát menu'.",
            parameters: { type: "OBJECT", properties: {} },
        },
        handler: async () => {
            try {
                if (typeof window !== "undefined") {
                    let closed = false;
                    if (typeof (window as any).closeSidebarMenu === "function") {
                        (window as any).closeSidebarMenu();
                        closed = true;
                    }
                    if (typeof (window as any).closeSidebar === "function") {
                        (window as any).closeSidebar();
                        closed = true;
                    }
                    window.dispatchEvent(new CustomEvent("close_sidebar_menu"));
                    return { success: true, message: "Đã đóng thanh menu.", instruction: "Xác nhận rõ ràng với người dùng rằng bạn đã đóng thanh menu." };
                }
                return { success: false, message: "Không thể đóng thanh menu." };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    // ==================== UI CONTROLS ====================
    close_tab: {
        declaration: {
            name: "close_tab",
            description:
                "Đóng trình xem ảnh, video, 3D model, tài liệu đang mở (Close photo/image/video/3D/tab viewer). Sử dụng khi người dùng yêu cầu: 'đóng ảnh', 'đóng trình xem ảnh', 'tắt ảnh', 'tắt hình', 'đóng video', 'tắt video', 'đóng xem ảnh', 'đóng tab', 'close photo', 'close image', 'close viewer', 'close tab'. Do NOT use this to close the location-recommendations popup (the 'Gợi ý địa điểm' card) — for that, use close_recommendations_popup instead.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            const isModelOpen = Boolean(
                (window as any).isGalleryDetail?.isOpen ||
                (typeof document !== 'undefined' && document.querySelector('[data-gallery-detail-view]'))
            );

            if (isModelOpen) {
                if (typeof (window as any).handleCloseGalleryDetail === 'function') {
                    (window as any).handleCloseGalleryDetail();
                }
                window.dispatchEvent(new CustomEvent("close_model"));
                if (typeof (window as any).openPanel === 'function') {
                    (window as any).openPanel("gallery");
                }
                if (typeof window.closeDrawerMenu === 'function') {
                    window.closeDrawerMenu("material");
                }
                if (typeof (window as any).silenceAllNonTourAudio === 'function') {
                    (window as any).silenceAllNonTourAudio();
                }
                return {
                    success: true,
                    message: "Đã đóng mô hình 3D và quay lại danh sách Hiện vật 3D.",
                    instruction: "Thông báo cho người dùng rằng bạn đã đóng mô hình 3D và quay lại danh sách Hiện vật 3D trong Thư viện 3D.",
                };
            }

            window.resetFunctionStatusToDefault?.();
            if (typeof window !== "undefined") {
                if (typeof window.onCloseImageModal === 'function') {
                    window.onCloseImageModal();
                }
                if (typeof window.oncloseVideoModal === 'function') {
                    window.oncloseVideoModal();
                }
                if (typeof window.onCloseDocumentModal === 'function') {
                    window.onCloseDocumentModal();
                }
                if (typeof window.closeMaterialDetail === 'function') {
                    window.closeMaterialDetail();
                }
                window.dispatchEvent(new CustomEvent("close_tab"));
                window.dispatchEvent(new CustomEvent("close_video"));
                window.dispatchEvent(new CustomEvent("close_image"));
                window.dispatchEvent(new CustomEvent("close_photo"));
                window.dispatchEvent(new CustomEvent("close_document"));
            }
            return { success: true, message: "Đã đóng trình xem nội dung thành công.", instruction: "Thông báo cho người dùng rằng bạn đã đóng trình xem." };
        },
    },

    close_video: {
        declaration: {
            name: "close_video",
            description:
                "Đóng trình xem video (Video Viewer / Video Modal) đang mở. Sử dụng khi người dùng yêu cầu: 'đóng video', 'tắt video', 'đóng xem video', 'dừng video', 'close video', 'exit video'.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            if (typeof window !== "undefined") {
                if (typeof window.oncloseVideoModal === 'function') {
                    window.oncloseVideoModal();
                }
                if (typeof window.closeMaterialDetail === 'function') {
                    window.closeMaterialDetail();
                }
                window.dispatchEvent(new CustomEvent("close_tab"));
                window.dispatchEvent(new CustomEvent("close_video"));
            }
            return { success: true, message: "Đã đóng trình xem video thành công.", instruction: "Thông báo cho người dùng rằng bạn đã đóng trình xem video." };
        },
    },

    close_photo: {
        declaration: {
            name: "close_photo",
            description:
                "Đóng trình xem ảnh chi tiết (Image Viewer) đang mở. Sử dụng khi người dùng yêu cầu: 'đóng ảnh', 'đóng trình xem ảnh', 'tắt ảnh', 'tắt hình', 'đóng xem ảnh', 'close photo', 'close image'.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            if (typeof window !== "undefined") {
                if (typeof window.onCloseImageModal === 'function') {
                    window.onCloseImageModal();
                } else if (typeof window.closeMaterialDetail === 'function') {
                    window.closeMaterialDetail();
                }
                window.dispatchEvent(new CustomEvent("close_tab"));
                window.dispatchEvent(new CustomEvent("close_image"));
                window.dispatchEvent(new CustomEvent("close_photo"));
            }
            return { success: true, message: "Đã đóng trình xem ảnh thành công.", instruction: "Thông báo cho người dùng rằng bạn đã đóng trình xem ảnh." };
        },
    },

    close_recommendations_popup: {
        declaration: {
            name: "close_recommendations_popup",
            description:
                "Close ONLY the location-recommendations popup (the 'Gợi ý địa điểm' card shown after show_recommendations). Use when the user asks to close/dismiss/hide the recommendations, suggestions, or the popup. Do NOT use close_tab for this — close_tab closes 3D/video/photo viewers and leaves the popup open.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("close_recommendations"));
            }
            return { success: true, message: "Recommendations popup closed", instruction: "Thông báo cho người dùng rằng bạn đã đóng popup gợi ý địa điểm." };
        },
    },

    show_recommendations: {
        declaration: {
            name: "show_recommendations",
            description:
                "REQUIRED whenever the user asks for nearby places, food, attractions, hotels, things to do, or recommendations around the current scene/location. You MUST call this function to display results in a popup BEFORE speaking the answer — never reply with a text-only list of places. Steps: (1) use Google Search to gather candidates, (2) call this function with at least 3 items per category you have data for, (3) then narrate the highlights. If a category has fewer than 3 results, pass what you have (empty array allowed) rather than skipping the call. To close this popup later, call close_recommendations_popup (NOT close_tab).",
            parameters: {
                type: "OBJECT",
                properties: {
                    location: {
                        type: "STRING",
                        description: "The location/city these recommendations are for (e.g., 'Hanoi', 'Ho Chi Minh City'). If user do not provide their current location, use function get_current_location to get it.",
                    },
                    eat: {
                        type: "ARRAY",
                        description: "List of at least 3 places to eat. Extract real restaurant names, descriptions, ratings, and addresses from search results.",
                        items: {
                            type: "OBJECT",
                            properties: {
                                name: { type: "STRING", description: "Restaurant/food place name" },
                                description: { type: "STRING", description: "Brief description of the cuisine or specialty" },
                                rating: { type: "NUMBER", description: "Rating from 1-5, estimate if not available" },
                                address: { type: "STRING", description: "Address or location area" },
                            },
                        },
                    },
                    fun: {
                        type: "ARRAY",
                        description: "List of at least 3 places to have fun (attractions, activities, entertainment). Extract real place names from search results.",
                        items: {
                            type: "OBJECT",
                            properties: {
                                name: { type: "STRING", description: "Attraction/activity name" },
                                description: { type: "STRING", description: "Brief description of the activity or attraction" },
                                rating: { type: "NUMBER", description: "Rating from 1-5, estimate if not available" },
                                address: { type: "STRING", description: "Address or location area" },
                            },
                        },
                    },
                    stay: {
                        type: "ARRAY",
                        description: "List of at least 3 places to stay (hotels, hostels, accommodations). Extract real hotel names from search results.",
                        items: {
                            type: "OBJECT",
                            properties: {
                                name: { type: "STRING", description: "Hotel/accommodation name" },
                                description: { type: "STRING", description: "Brief description of the accommodation type and amenities" },
                                rating: { type: "NUMBER", description: "Rating from 1-5, estimate if not available" },
                                address: { type: "STRING", description: "Address or location area" },
                            },
                        },
                    },
                },
                required: ["location", "eat", "fun", "stay"],
            },
        },
        handler: async (args: any) => {
            console.log("🎯 show_recommendations called with:", JSON.stringify(args, null, 2));
            console.log("📍 Location:", args.location);
            console.log("🍽️ Eat places:", args.eat?.length || 0);
            console.log("🎉 Fun places:", args.fun?.length || 0);
            console.log("🏨 Stay places:", args.stay?.length || 0);

            // Dispatch custom event for components that might be listening
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('show_recommendations', {
                    detail: args
                }));
            }

            return {
                success: true,
                message: `Recommendations for ${args.location} displayed successfully with ${args.eat?.length || 0} restaurants, ${args.fun?.length || 0} activities, and ${args.stay?.length || 0} hotels.`,
                instruction: `BẮT BUỘC phải giới thiệu cho người dùng các gợi ý địa điểm tại ${args.location} vừa hiển thị trên màn hình. Trình bày tự nhiên, hấp dẫn.`
            };
        },
    },

    // ==================== FACILITY DEMO (QTSC) ====================
    show_facility_demo_cards: {
        declaration: {
            name: "show_facility_demo_cards",
            description:
                "Open the 3D model of equipment and its Description (Mô tả) tab. Use when the user asks to see an equipment 3D model with operating guide, SOP, manual, maintenance procedure, or tutorial for facility equipment such as valves, pumps, pipes, or generators. Example triggers: 'mở model 3D hệ thống van', 'xem hướng dẫn thao tác', 'trích xuất SOP', 'mở tài liệu vận hành'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    equipment_name: {
                        type: "STRING",
                        description: "Name of the equipment to display (e.g. 'hệ thống van cấp nước', 'bơm', 'máy phát điện')",
                    },
                },
                required: ["equipment_name"],
            },
        },
        handler: async (args: any) => {
            try {
                let modelData = null;
                let modelOpened = false;

                // Search for the real 3D model in the project database
                if (typeof window !== 'undefined') {
                    try {
                        let targetModelName = args.equipment_name;
                        if (window?.id_path === '21212121') {
                            const term = targetModelName.toLowerCase();
                            if (term.includes('van') || term.includes('nước') || term.includes('piping') || term.includes('valve') || term.includes('water')) {
                                targetModelName = 'Piping system components';
                            }
                        }
                        const response = await functionCallingServices.openModel(window?.id_path, targetModelName);
                        console.log('[show_facility_demo_cards] openModel response:', response);

                        let uuid = response?.data?.uuid || response?.uuid;
                        if (!uuid) {
                            try {
                                const searchRes = await functionCallingServices.searchModelIdByName(window?.id_path, targetModelName);
                                uuid = searchRes?.uuid || searchRes?.data?.uuid;
                            } catch (e) {
                                console.warn('[show_facility_demo_cards] searchModelIdByName failed:', e);
                            }
                        }

                        if (uuid) {
                            modelData = response.data || {};
                            modelOpened = true;
                            // Open the actual 3D model viewer
                            window?.resetFunctionStatusToDefault?.();
                            (window as any).agentOpenedModel = true;
                            window.handleOpenGalleryDetail?.(window?.id_path, uuid);
                        } else {
                            console.warn('[show_facility_demo_cards] UUID missing, fallback to overlay only');
                        }
                    } catch (e) {
                        console.warn('[show_facility_demo_cards] Could not find 3D model, showing overlay only:', e);
                    }
                } // <--- This closes the `if (typeof window !== 'undefined')`

                // In the updated flow, we DO NOT show the custom 3-card overlay anymore for this step.
                // We use the actual 3D model viewer and its info panel (Mô tả tab).
                if (typeof window !== 'undefined') {
                    // Ensure we start on the description tab
                    setTimeout(() => {
                        window.dispatchEvent(
                            new CustomEvent('switch_gallery_tab', { detail: { tab: 'description' } })
                        );
                    }, 500);
                }
                if (typeof window !== 'undefined' && (window?.id_path === '20251210' || window?.id_path === '20260812')) {
                    const exactScriptEn = "Here is the 3D Model of the valve cluster. I have also attached the SOP document and a 10-second instruction video. Please be sure to pull out the safety pin before turning the valve clockwise.";
                    return {
                        success: true,
                        message: `Opened 3D Model for "${args.equipment_name}". NOW READ THE SCRIPT ALOUD EXACTLY.`,
                        instruction: `You MUST now speak exactly the following sentence aloud in English ONLY: "${exactScriptEn}"`,
                    };
                }

                return {
                    success: true,
                    message: `Opened 3D Model for "${args.equipment_name}" and its Description (Mô tả) tab.`,
                    instruction: `Thông báo cho người dùng rằng bạn đã mở mô hình 3D của thiết bị "${args.equipment_name}" và tab Mô tả.`
                };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    close_facility_demo: {
        declaration: {
            name: "close_facility_demo",
            description: "Close the facility demo overlay (the 3 cards or the incident report). Use when the user says 'đóng', 'tắt', 'thu gọn' regarding the SOP, reports, or demo cards.",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('close_facility_demo'));
            }
            return {
                success: true,
                message: "Facility demo overlay closed successfully.",
                instruction: "Thông báo cho người dùng rằng bạn đã đóng phần demo."
            };
        },
    },

    switch_gallery_tab: {
        declaration: {
            name: "switch_gallery_tab",
            description: "Switch the active tab in the 3D model's Information Panel (e.g. from Description/Mô tả to Video). Use when the user asks to switch to the video tab, article tab, or images tab of the currently open model.",
            parameters: {
                type: "OBJECT",
                properties: {
                    tab_name: {
                        type: "STRING",
                        description: "The ID of the tab to switch to. Must be one of: 'description' (Mô tả), 'article' (Bài viết), 'images' (Hình ảnh), 'video' (Video), 'artifact' (Hiện vật).",
                    },
                },
                required: ["tab_name"],
            },
        },
        handler: async (args: any) => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('switch_gallery_tab', { detail: { tab: args.tab_name } }));
            }
            return {
                success: true,
                message: `Switched to tab: ${args.tab_name}.`,
                instruction: `Thông báo cho người dùng rằng bạn đã chuyển sang tab ${args.tab_name}.`
            };
        },
    },

    toggle_gallery_info_panel: {
        declaration: {
            name: "toggle_gallery_info_panel",
            description: "Show or hide the Information Panel (description tab) of the currently open 3D model. Use this when the user asks to hide or close the description/info panel while keeping the 3D model visible, or when they ask to show it again.",
            parameters: {
                type: "OBJECT",
                properties: {
                    show: {
                        type: "BOOLEAN",
                        description: "True to show the info panel, false to hide it.",
                    },
                },
                required: ["show"],
            },
        },
        handler: async (args: any) => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('toggle_gallery_info_panel', { detail: { show: args.show } }));
            }
            return {
                success: true,
                message: args.show ? "Information panel is now shown." : "Information panel is now hidden.",
                instruction: args.show ? "Thông báo cho người dùng rằng bạn đã hiển thị bảng thông tin." : "Thông báo cho người dùng rằng bạn đã ẩn bảng thông tin."
            };
        },
    },

    open_minimap_qtsc: {
        declaration: {
            name: "open_minimap_qtsc",
            description: "Open the minimap and filter the area reporting the error and surrounding risk zones for QTSC. Use this when the user asks to open the minimap to check the error location or risk zones (e.g. 'Mở minimap, lọc khu vực đang báo lỗi', 'Open the minimap, filter the area reporting the error').",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('toggle_qtsc_minimap', { detail: { show: true } }));
            }
            const exactScriptEn = "The reporting Minimap is opened. The red dot is the incident location. The green area is safe. The water level is at risk of overflowing into adjacent rooms in the QTSC 3 building.";
            return {
                success: true,
                message: "QTSC Minimap opened successfully with error filters applied.",
                instruction: `You MUST speak exactly the following sentence aloud in English ONLY: "${exactScriptEn}"`,
            };
        },
    },

    close_minimap_qtsc: {
        declaration: {
            name: "close_minimap_qtsc",
            description: "Close the QTSC minimap. Use this when the user asks to close or hide the minimap (e.g. 'Đóng minimap', 'Close the minimap').",
            parameters: {
                type: "OBJECT",
                properties: {},
            },
        },
        handler: async () => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('toggle_qtsc_minimap', { detail: { show: false } }));
            }
            return {
                success: true,
                message: "QTSC Minimap closed successfully.",
            };
        },
    },

    resolve_incident_generate_report: {
        declaration: {
            name: "resolve_incident_generate_report",
            description:
                "Mark the current incident as resolved and auto-generate a downloadable PDF incident report. Use when the user confirms a maintenance task is completed (e.g. valve locked, equipment fixed) and asks for an incident report, summary, or documentation. Example triggers: 'đã khóa van', 'xong rồi, làm báo cáo', 'tạo biên bản sự cố', 'hỗ trợ tóm tắt và làm báo cáo'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    incident_summary: {
                        type: "STRING",
                        description: "Brief summary of what happened and how it was resolved",
                    },
                    user_language: {
                        type: "STRING",
                        description: "The language the user is speaking, e.g. 'en' for English, 'vi' for Vietnamese. Default to 'vi' if unknown.",
                    },
                },
                required: ["incident_summary", "user_language"],
            },
        },
        handler: async (args: any) => {
            try {
                const now = new Date();
                const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;

                if (typeof window !== 'undefined') {
                    window.dispatchEvent(
                        new CustomEvent('resolve_incident_generate_report', {
                            detail: {
                                summary: args.incident_summary,
                                timestamp: now.toISOString(),
                                fileName: `Report_${dateStr}_Incident.pdf`,
                                language: 'en', // Force English for demo
                            },
                        })
                    );
                }

                const narrationEn = "Great job! The incident has been resolved. I have automatically generated a standard report in PDF format. You can click to download it right now.";

                return {
                    success: true,
                    message: `Incident resolved. PDF report generated and is ready for download on screen.`,
                    instruction: `You MUST confirm to the user in English ONLY: "${narrationEn}"`,
                };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    get_shortest_route: {
        declaration: {
            name: "get_shortest_route",
            description:
                "Open direction map / route guide to calculate and display the shortest path from current location to destination, including navigating to the IoT device with an issue / incident location (e.g. 'dẫn đường đến chỗ thiết bị IoT có sự cố', 'chỉ đường đến chỗ rò rỉ nước', 'đường đi ngắn nhất đến vị trí sự cố', 'dẫn đường ngắn nhất để đến', 'navigate to the IoT device with issue', 'how to get to the incident location').",
            parameters: {
                type: "OBJECT",
                properties: {
                    destination: {
                        type: "STRING",
                        description: "Destination name or address if specified by user"
                    }
                }
            }
        },
        handler: async (args: any) => {
            try {
                let currentIdPath: string | null = null;
                if (typeof window !== 'undefined') {
                    currentIdPath = (window as any)?.id_path || (window as any)?.idPath;
                    window.dispatchEvent(new CustomEvent('open_direction_route', {
                        detail: {
                            destination: args?.destination,
                            id_path: currentIdPath
                        }
                    }));
                }

                const isProject20251210 = currentIdPath === '20251210' || currentIdPath === '20260812';

                const narrationVn = "Đang khởi chạy Google Maps tự động dẫn đường cho bạn đến khu vực kỹ thuật.";
                const narrationEn = "Launching Google Maps to automatically navigate you to the Technical Area.";

                return {
                    success: true,
                    message: "Shortest route navigation map displayed successfully.",
                    instruction: `Confirm to the user in the EXACT SAME LANGUAGE they spoke (do NOT force Vietnamese if the user spoke English or another language). 
                    - Vietnamese response template: "${narrationVn}"
                    - English response template: "${narrationEn}"
                    - Other languages: Translate the response naturally into the user's spoken language.`,
                    narration_vn: narrationVn,
                    narration_en: narrationEn
                };
            } catch (error: any) {
                return { success: false, error: error.message };
            }
        },
    },

    get_artifact_details: {
        declaration: {
            name: "get_artifact_details",
            description: "Tra cứu hồ sơ lưu trữ chính thức của hiện vật 3D (tên, mô tả, chất liệu, tác giả, niên đại, xuất xứ, nơi lưu giữ) trong dự án bảo tàng/di sản hiện tại. BẮT BUỘC gọi công cụ này khi người dùng hỏi về thông tin, nguồn gốc, câu chuyện, chất liệu hoặc chi tiết của hiện vật 3D đang xem, sau đó kết hợp thông tin hồ sơ với hình ảnh góc nhìn hiện tại để trả lời.",
            parameters: {
                type: "OBJECT",
                properties: {
                    artifact_id: {
                        type: "STRING",
                        description: "Mã định danh (UUID), tên hiện vật, hoặc 'current' (hoặc để trống) để tra cứu hồ sơ hiện vật 3D đang mở trên màn hình.",
                    },
                    language: {
                        type: "STRING",
                        description: "Ngôn ngữ của hồ sơ: 'vn' (tiếng Việt) hoặc 'en' (tiếng Anh). Mặc định 'vn'.",
                    },
                },
            },
        },
        handler: async (args: any) => {
            console.log('[get_artifact_details] Called with args:', args);

            const projectId = (typeof window !== 'undefined' && window?.id_path)
                ? String(window.id_path).trim()
                : '';

            if (!projectId) {
                return {
                    success: false,
                    error: "Không tìm thấy mã dự án hiện tại (id_path). Vui lòng đảm bảo đang ở trong trang bảo tàng/di sản.",
                };
            }

            const parsed = parseAndNormalizeArtifactId(args?.artifact_id);

            // Input validation: reject prototype pollution or malicious input
            if (parsed.isInvalid) {
                return {
                    success: false,
                    error: "Tham số artifact_id không hợp lệ.",
                };
            }

            const rawLang = typeof args?.language === 'string' ? args.language.trim().toLowerCase() : '';
            let lang: string;
            if (rawLang === 'en' || rawLang === 'english') {
                lang = 'en';
            } else if (rawLang === 'vn' || rawLang === 'vi' || rawLang === 'vietnamese') {
                lang = 'vn';
            } else {
                const uiLang = typeof window !== 'undefined' && typeof window.getCurrentLanguage === 'function'
                    ? window.getCurrentLanguage()?.id?.toLowerCase()
                    : undefined;
                lang = (uiLang === 'en' || uiLang === 'english') ? 'en' : 'vn';
            }

            let resolvedUuid = '';

            if (parsed.isCurrent) {
                const activeId = activeVisualSourceRegistry.getActiveModelId?.()
                    || (typeof window !== 'undefined' && (window as any).__activeModelId)
                    || (typeof window !== 'undefined' && (window as any).__currentActiveModelId);

                if (activeId && typeof activeId === 'string' && activeId.trim()) {
                    resolvedUuid = normalizeModelId(activeId) || activeId.trim();
                } else {
                    return {
                        success: false,
                        error: "Hiện tại không có hiện vật 3D nào đang được mở trên màn hình. Hãy yêu cầu người dùng chỉ định tên hiện vật.",
                    };
                }
            } else if (parsed.isUuid && parsed.normalizedUuid) {
                resolvedUuid = parsed.normalizedUuid;
            } else {
                // Search model by name: preserve original search casing
                const searchTerm = parsed.searchTerm || parsed.trimmed;
                try {
                    const searchRes = await functionCallingServices.searchModelIdByName(projectId, searchTerm, lang);
                    const foundUuid = searchRes?.uuid || searchRes?.data?.uuid;
                    if (foundUuid) {
                        resolvedUuid = normalizeModelId(foundUuid) || foundUuid;
                    } else {
                        // Try openModel search as fallback
                        const openRes = await functionCallingServices.openModel(projectId, searchTerm, lang);
                        const fallbackUuid = openRes?.data?.uuid || openRes?.uuid;
                        if (fallbackUuid) {
                            resolvedUuid = normalizeModelId(fallbackUuid) || fallbackUuid;
                        } else {
                            return {
                                success: false,
                                error: `Không tìm thấy hiện vật nào có tên hoặc mã "${searchTerm}" trong dự án bảo tàng này.`,
                            };
                        }
                    }
                } catch (e: any) {
                    console.warn('[get_artifact_details] searchModelIdByName failed:', e);
                    return {
                        success: false,
                        error: `Không thể tìm kiếm hiện vật "${searchTerm}": ${e?.message || 'Lỗi mạng.'}`,
                    };
                }
            }

            // Check cache
            const cacheKey = `${projectId}:${resolvedUuid}:${lang}`;
            const cached = artifactDetailsCache.get(cacheKey);
            if (cached && (Date.now() - cached.timestamp) < ARTIFACT_CACHE_TTL_MS) {
                console.log(`[get_artifact_details] Cache hit for ${cacheKey}`);
                return cached.data;
            }

            try {
                const response = await galleryServices.getGalleryById(resolvedUuid, projectId, lang);
                const asset = response?.data || response;

                if (!asset || (!asset.uuid && !asset.id && !asset.attributes && !asset.name && !asset.title)) {
                    return {
                        success: false,
                        error: `Không tìm thấy hồ sơ chi tiết cho hiện vật (mã: ${resolvedUuid}).`,
                    };
                }

                const title = sanitizeArtifactText(
                    extractMultilingualArtifactField(asset, ['title', 'name', 'ten_goi'], lang) || 'Hiện vật',
                    200
                );

                const description = sanitizeArtifactText(
                    extractMultilingualArtifactField(asset, ['mieu_ta', 'content', 'description', 'mo_ta'], lang),
                    1500
                );

                const material = sanitizeArtifactText(
                    extractMultilingualArtifactField(asset, ['material', 'chat_lieu'], lang),
                    200
                );

                const era = sanitizeArtifactText(
                    extractMultilingualArtifactField(asset, ['era', 'time_period', 'nien_dai', 'thoi_ky'], lang),
                    200
                );

                const author = sanitizeArtifactText(
                    extractMultilingualArtifactField(asset, ['author', 'tac_gia', 'nghe_nhan'], lang),
                    200
                );

                const origin = sanitizeArtifactText(
                    extractMultilingualArtifactField(asset, ['origin', 'xuat_xu', 'nguon_goc', 'dia_diem'], lang),
                    200
                );

                const source = sanitizeArtifactText(
                    extractMultilingualArtifactField(asset, ['source', 'noi_luu_tru', 'noi_luu_giu', 'storage_location'], lang),
                    200
                );

                const result = {
                    success: true,
                    artifact: {
                        id: resolvedUuid,
                        name: title,
                        description: description || 'Chưa có mô tả chi tiết trong hồ sơ.',
                        material: material || undefined,
                        era: era || undefined,
                        author: author || undefined,
                        origin: origin || undefined,
                        source: source || undefined,
                    },
                    instruction: "Đây là hồ sơ lưu trữ chính thức của hiện vật. Hãy kết hợp thông tin hồ sơ này với hình ảnh góc nhìn hiện tại của người dùng để trả lời câu hỏi một cách tự nhiên và sinh động.",
                };

                artifactDetailsCache.set(cacheKey, { data: result, timestamp: Date.now() });
                return result;
            } catch (error: any) {
                console.error('[get_artifact_details] Error fetching artifact dossier:', error);
                return {
                    success: false,
                    error: `Lỗi kết nối khi tra cứu hồ sơ hiện vật: ${error?.message || 'Không phản hồi'}`,
                };
            }
        },
    },
};

// ==================== PORTAL TOOLS DEFINITIONS ====================
export const portalTools: Record<string, GeminiTool> = {
    get_artifact_details: tools.get_artifact_details,
    get_sectors: {
        declaration: {
            name: "get_sectors",
            description: "Retrieve a nested list of all project sectors/categories (e.g., Tourism, Heritage, Smart City) and automatically opens the sector list panel. Use this when the user asks 'What categories of projects do you have?', 'List your business sectors', or any question related to the list of fields/sectors.",
            parameters: {
                type: "OBJECT",
                properties: {},
            }
        },
        handler: async () => {
            const result = await functionCallingServices.getSectors();
            try {
                await functionCallingServices.openSectorList();
            } catch (e) {
                console.warn('Failed to call openSectorList API:', e);
            }
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('open_sector_list'));
            }
            return result;
        }
    },
    get_projects_by_location: {
        declaration: {
            name: "get_projects_by_location",
            description: "Retrieve a list of projects or search for projects by name or by sector/category. REQUIRED when user asks about what projects exist in a specific sector (e.g. 'những dự án nào thuộc lĩnh vực hạ tầng', 'dự án nào trong lĩnh vực di sản'). Use this when the user asks to 'list projects', 'search for project [name]', or 'find projects in [sector/field]'.",
            parameters: {
                type: "OBJECT",
                properties: {
                    search: {
                        type: "STRING",
                        description: "The search keyword to find projects by name or description."
                    },
                    field: {
                        type: "STRING",
                        description: "The sector/field name MUST BE ONE OF: 'city', 'exhibition', 'facility', 'factory', 'farm', 'heritage', 'museum', 'office', 'oil-and-gas', 'real-estate', 'school', 'showroom', 'tourism', 'tphcm-dbscl', 'training', 'virtual-showroom', 'zone'. Select the exact matching ID based on the user's Vietnamese request (e.g., 'dầu khí' -> 'oil-and-gas', 'hạ tầng' -> 'facility', 'bất động sản' -> 'real-estate', 'giáo dục' -> 'school', 'nhà máy' -> 'factory', 'công nghiệp' / 'khu công nghiệp' -> 'zone')."
                    },
                    page: {
                        type: "NUMBER",
                        description: "Page number for pagination. Defaults to 1."
                    },
                    limit: {
                        type: "NUMBER",
                        description: "Number of projects to return per page. Defaults to 20."
                    }
                }
            }
        },
        handler: async (args: any) => {
            return await functionCallingServices.getProjectsByLocation(args.search, args.field, args.page, args.limit);
        }
    },
    open_sector_list: {
        declaration: {
            name: "open_sector_list",
            description: "Mở danh sách các lĩnh vực (sectors/categories) trên giao diện. Sử dụng công cụ này khi người dùng yêu cầu mở hoặc xem danh sách các lĩnh vực hoạt động, lĩnh vực áp dụng công nghệ.",
            parameters: {
                type: "OBJECT",
                properties: {},
            }
        },
        handler: async () => {
            const result = await functionCallingServices.openSectorList();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('open_sector_list'));
            }
            return result;
        }
    },
    close_sector_list: {
        declaration: {
            name: "close_sector_list",
            description: "Đóng danh sách các lĩnh vực trên giao diện. Sử dụng công cụ này khi người dùng yêu cầu đóng danh sách lĩnh vực hoặc quay lại trang chủ.",
            parameters: {
                type: "OBJECT",
                properties: {},
            }
        },
        handler: async () => {
            const result = await functionCallingServices.closeSectorList();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('close_sector_list'));
            }
            return result;
        }
    },
    open_sector_project_list: {
        declaration: {
            name: "open_sector_project_list",
            description: "Chuyển hướng mở màn hình Danh sách dự án trong một lĩnh vực cụ thể. Sử dụng công cụ này KẾT HỢP VỚI hàm get_projects_by_location (ví dụ: get_projects_by_location để lấy dữ liệu json => open_sector_project_list mở UI) để người dùng xem được ngay danh sách dự án trên màn hình lúc thuyết minh.",
            parameters: {
                type: "OBJECT",
                properties: {
                    sector: {
                        type: "STRING",
                        description: "Mã id_field của lĩnh vực cần chuyển sang. Phải là một trong các giá trị sau: 'city' (Đô thị), 'exhibition' (Triển lãm), 'facility' (Hạ tầng), 'factory' (Nhà máy), 'farm' (Trang trại / Nông nghiệp), 'heritage' (Di sản), 'museum' (Bảo tàng), 'office' (Văn phòng), 'oil-and-gas' (Dầu khí), 'real-estate' (Bất động sản), 'school' (Trường học), 'showroom' (Showroom / Trưng bày), 'tourism' (Du lịch), 'zone' (Khu công nghiệp / Công nghiệp)."
                    }
                },
                required: ["sector"]
            }
        },
        handler: async (args: any) => {
            const result = await functionCallingServices.openSectorProjectList();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('open_sector_project_list', { detail: { sector: args.sector } }));
            }
            return result;
        }
    },
    close_sector_project_list: {
        declaration: {
            name: "close_sector_project_list",
            description: "Đóng màn hình danh sách dự án của một lĩnh vực đang mở (giúp quay lại màn hình trước đó).",
            parameters: {
                type: "OBJECT",
                properties: {},
            }
        },
        handler: async () => {
            const result = await functionCallingServices.closeSectorProjectList();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('close_sector_project_list'));
            }
            return result;
        }
    },
    open_project: {
        declaration: {
            name: "open_project",
            description: "Mở xem chi tiết một dự án (mở trong tab mới). Bạn gọi hàm này sau khi đã có thông tin URL của dự án từ hàm get_projects_by_location (thuộc tính url hoặc URL).",
            parameters: {
                type: "OBJECT",
                properties: {
                    project_url: {
                        type: "STRING",
                        description: "Đường dẫn URL của dự án muốn mở (Ví dụ: 'https://smart-tourism...)!"
                    }
                },
                required: ["project_url"]
            }
        },
        handler: async (args: any) => {
            console.log("🛠️ open_project triggered, URL:", args.project_url);
            const result = await functionCallingServices.openProject(args.project_url);
            if (typeof window !== 'undefined' && args.project_url) {
                const a = document.createElement('a');
                a.href = args.project_url;
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
            return result;
        }
    },
    open_map: {
        declaration: {
            name: "open_map",
            description: "Mở bản đồ số hóa 3D (Map 3D) trên trang chủ. Sử dụng công cụ này khi người dùng yêu cầu mở bản đồ, xem bản đồ số hóa, xem bản đồ 3D.",
            parameters: {
                type: "OBJECT",
                properties: {}
            }
        },
        handler: async () => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('open_map_directions'));
            }
            return { success: true, message: "Đã mở bản đồ số hóa." };
        }
    },
    open_map_directions: {
        declaration: {
            name: "open_map_directions",
            description: "Chỉ dùng khi người dùng hỏi rõ về hướng dẫn đường đi, chỉ đường, đường đi, dẫn đường hoặc route tới một dự án cụ thể. Công cụ này sẽ mở chế độ bản đồ chỉ đường và có thể xin định vị người dùng.",
            parameters: {
                type: "OBJECT",
                properties: {
                    project_url: { type: "STRING", description: "URL dự án để chỉ đường tới dự án. Bắt buộc truyền khi người dùng muốn hướng dẫn đường đi tới dự án cụ thể." }
                }
            }
        },
        handler: async (args: any) => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('open_map_directions', { detail: { project_url: args?.project_url } }));
            }
            return { success: true, message: "Đã mở bản đồ số hóa hiển thị thông tin dự án." };
        }
    },
    focus_map_project: {
        declaration: {
            name: "focus_map_project",
            description: "Đưa bản đồ zoom tới gần một dự án cụ thể và mở popup thông tin dự án. Chỉ dùng khi người dùng muốn xem/tới/xem vị trí một dự án, nhưng KHÔNG hỏi hướng dẫn đường đi.",
            parameters: {
                type: "OBJECT",
                properties: {
                    project_url: { type: "STRING", description: "URL dự án cần zoom tới, lấy từ trường url trong danh sách projects của filter_map_projects hoặc get_projects_by_location." }
                },
                required: ["project_url"]
            }
        },
        handler: async (args: any) => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('focus_map_project', { detail: { project_url: args?.project_url } }));
            }
            return { success: true, message: "Đã đưa bản đồ tới dự án và mở popup thông tin dự án." };
        }
    },
    filter_map_projects: {
        declaration: {
            name: "filter_map_projects",
            description: "Lọc hiển thị dự án trên bản đồ số hóa theo miền, lĩnh vực, hoặc các tỉnh thành cụ thể khi người dùng yêu cầu lọc/tìm kiếm. Sử dụng công cụ này để lọc trực quan các dự án trên bản đồ.",
            parameters: {
                type: "OBJECT",
                properties: {
                    region: { type: "STRING", description: "Miền để lọc (chỉ được phép truyền: 'Miền Bắc', 'Miền Trung', 'Miền Nam'). Người dùng có thể nói bằng tiếng Nhật (ví dụ: '北部' → 'Miền Bắc', '中部' → 'Miền Trung', '南部' → 'Miền Nam') — hãy dịch sang tiếng Việt trước khi truyền. Có thể để trống nếu không lọc theo miền." },
                    fields: { type: "ARRAY", items: { type: "STRING" }, description: "Mảng các lĩnh vực cần lọc, bắt buộc phải lấy đúng các mã sau (không dùng tiếng Việt): 'tourism' (du lịch / 観光), 'heritage' (di sản / 遺産), 'museum' (bảo tàng / 博物館), 'city' (thành phố / 都市), 'oil-and-gas' (dầu khí / 石油ガス), 'factory' (nhà máy / 工場), 'zone' (khu công nghiệp / 工業団地), 'facility' (hạ tầng / インフラ), 'showroom' (showroom / ショールーム), 'real-estate' (bất động sản / 不動産), 'exhibition' (triển lãm / 展示会), 'office' (văn phòng / オフィス), 'school' (trường học / 学校), 'farm' (trang trại / 農場). Nếu người dùng nói bằng tiếng Nhật (ví dụ: '観光'), hãy dịch sang mã tiếng Anh tương ứng ('tourism')." },
                    provinces: { type: "ARRAY", items: { type: "STRING" }, description: "Mảng tên các tỉnh thành cụ thể cần lọc (ví dụ: ['Bến Tre', 'Đồng Nai', 'Hà Nội', 'TP. Hồ Chí Minh']). Viết chính xác tiếng Việt có dấu. Nếu người dùng nói bằng tiếng Nhật (ví dụ: 'ハノイ' → 'Hà Nội', 'ホーチミン' → 'TP. Hồ Chí Minh'), hãy dịch sang tiếng Việt trước khi truyền." },
                    country: { type: "STRING", description: "Tên quốc gia để lọc (ví dụ: 'Việt Nam'). Nếu người dùng nói bằng tiếng Nhật (ví dụ: 'ベトナム' → 'Việt Nam', 'アメリカ' → 'Mỹ', 'カナダ' → 'Canada', 'オーストラリア' → 'Úc'), hãy dịch sang tiếng Việt trước khi truyền. Truyền 'nước ngoài' hoặc 'abroad' khi muốn xem các dự án ở nước ngoài." }
                }
            }
        },
        handler: async (args: any) => {
            if (typeof window !== 'undefined') {
                const requestId = `map_filter_${Date.now()}_${Math.random().toString(36).slice(2)}`;
                const detail = { requestId, region: args?.region, fields: args?.fields, provinces: args?.provinces, country: args?.country };
                (window as any).__pendingAiFilterData = detail;
                window.dispatchEvent(new CustomEvent('filter_map_projects', { detail }));

                const result = await waitForMapFilterResult(requestId);
                if (result) {
                    const hasInvalidProvinces = result.invalidProvinces && result.invalidProvinces.length > 0;
                    return {
                        success: true,
                        message: hasInvalidProvinces
                            ? `Đã áp dụng bộ lọc nhưng phát hiện một số tỉnh thành không tồn tại trong chế độ bản đồ 34 tỉnh. Chi tiết: ${result.invalidProvinces.map((p: any) => p.message).join(' ')}`
                            : `Đã áp dụng bộ lọc trên bản đồ và tìm thấy ${result.total} dự án phù hợp.`,
                        total: result.total,
                        invalidProvinces: result.invalidProvinces || [],
                        projects: result.projects,
                        guidance: hasInvalidProvinces
                            ? "CRITICAL RESPONSE INSTRUCTION: Bạn phải thông báo ngay cho người dùng bằng tiếng Việt rằng tỉnh thành họ yêu cầu không nằm trong danh sách 34 tỉnh do đã được sáp nhập (nêu chi tiết đã gộp vào tỉnh mới nào dựa theo thông tin trong trường 'invalidProvinces' ở trên). Gợi ý họ chuyển sang chế độ bản đồ 63 tỉnh bằng cách nói 'Anh/chị có muốn chuyển sang bản đồ 63 tỉnh thành để xem tỉnh này không?'."
                            : "Hãy tóm tắt ngắn gọn các dự án phù hợp bằng đúng ngôn ngữ người dùng đang sử dụng. Nếu người dùng muốn xem một dự án cụ thể, hãy gọi focus_map_project với project_url để zoom tới dự án và mở popup. Chỉ gọi open_map_directions khi người dùng hỏi rõ về hướng dẫn đường đi hoặc chỉ đường."
                    };
                }
            }
            return { success: true, message: "Đã yêu cầu áp dụng bộ lọc trên bản đồ." };
        }
    },
    close_map: {
        declaration: {
            name: "close_map",
            description: "Đóng hoàn toàn bản đồ số hóa (Map 3D) đang hiển thị và thoát khỏi chế độ xem bản đồ để trở về trang chủ website starglobal3d.com. Chỉ sử dụng công cụ này khi người dùng nói rõ là muốn ĐÓNG bản đồ số hóa, THOÁT bản đồ số hóa hoặc TẮT bản đồ số hóa. TUYỆT ĐỐI KHÔNG dùng công cụ này khi người dùng chỉ muốn quay lại toàn cảnh của bản đồ hiện tại, hủy bộ lọc hoặc xem dự án khác.",
            parameters: {
                type: "OBJECT",
                properties: {},
            }
        },
        handler: async () => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('close_map'));
            }
            return { success: true, message: "Đã đóng bản đồ số hóa." };
        }
    },
    switch_province_mode: {
        declaration: {
            name: "switch_province_mode",
            description: "Chuyển đổi chế độ hiển thị bản đồ số hóa giữa 34 tỉnh/thành và 63 tỉnh/thành. Chỉ gọi công cụ này khi người dùng YÊU CẦU ĐỔI BẢN ĐỒ hoặc yêu cầu chuyển đổi cụ thể sang chế độ bản đồ khác (ví dụ: 'chuyển sang bản đồ 63 tỉnh', 'chuyển sang bản đồ 34 tỉnh'). TUYỆT ĐỐI KHÔNG tự động chuyển đổi hoặc gọi công cụ này khi người dùng chỉ muốn quay lại toàn cảnh, reset bản đồ hiện tại hoặc hủy bộ lọc của bản đồ hiện tại.",
            parameters: {
                type: "OBJECT",
                properties: {
                    mode: {
                        type: "STRING",
                        description: "Chế độ bản đồ hiển thị, chỉ được phép truyền: '34' (cho bản đồ 34 tỉnh) hoặc '63' (cho bản đồ 63 tỉnh)."
                    }
                },
                required: ["mode"]
            }
        },
        handler: async (args: any) => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('switch_province_mode', { detail: { mode: args.mode } }));
            }
            return { success: true, message: `Đã chuyển đổi bản đồ sang chế độ hiển thị ${args.mode} tỉnh thành.` };
        }
    },
    reset_map_filter: {
        declaration: {
            name: "reset_map_filter",
            description: "Đặt lại toàn bộ bộ lọc dự án trên bản đồ hiện tại, quay lại hiển thị toàn cảnh (zoom out) của bản đồ hiện tại và hiển thị lại toàn bộ dự án. Đây là công cụ duy nhất được sử dụng khi người dùng yêu cầu quay lại, quay lại toàn cảnh, hủy bộ lọc, xem lại tất cả dự án, hoặc reset bản đồ hiện tại. Công cụ này giữ nguyên bản đồ số hóa đang mở và giữ nguyên chế độ bản đồ hiện tại (34 hoặc 63 tỉnh).",
            parameters: {
                type: "OBJECT",
                properties: {}
            }
        },
        handler: async () => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('reset_map_filter'));
            }
            return { success: true, message: "Đã đặt lại bộ lọc và quay lại toàn cảnh bản đồ." };
        }
    },
    close_map_directions: {
        declaration: {
            name: "close_map_directions",
            description: "Tắt hoặc đóng chế độ chỉ đường/dẫn đường trên bản đồ số hóa (Map 3D) để quay lại chế độ xem bản đồ thông thường. Chỉ gọi khi người dùng yêu cầu tắt chỉ đường, tắt dẫn đường, thoát chế độ dẫn đường hoặc không muốn chỉ đường nữa. TUYỆT ĐỐI KHÔNG dùng close_map khi người dùng chỉ muốn dừng chỉ đường.",
            parameters: {
                type: "OBJECT",
                properties: {}
            }
        },
        handler: async () => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('close_map_directions'));
            }
            return { success: true, message: "Đã tắt chế độ dẫn đường và quay lại bản đồ số hóa." };
        }
    },
    propose_navigation_links: {
        declaration: {
            name: "propose_navigation_links",
            description: "Propose a list of rooms/scenes to link from the current scene. Use when the user says: 'Tạo liên kết đến Phòng Khách, Phòng Ăn, Nhà bếp', 'Liên kết đến sảnh chính', or similar commands to bulk-create navigation hotspots.",
            parameters: {
                type: "OBJECT",
                properties: {
                    destinations: {
                        type: "ARRAY",
                        items: { type: "STRING" },
                        description: "List of scene titles or names to link to, e.g. ['Phòng Khách', 'Phòng Ăn', 'Nhà bếp']",
                    }
                },
                required: ["destinations"]
            }
        },
        handler: async (args: any) => {
            console.log('🤖 propose_navigation_links called with:', args);
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('propose_navigation_links', {
                    detail: { destinations: args.destinations }
                }));
            }
            return { success: true, message: `Proposed links to: ${args.destinations.join(', ')}` };
        }
    },

    // ==================== HYBRID PROMPT PIPELINE (Task 1.4) ====================
    retrieve_object_knowledge: {
        declaration: {
            name: "retrieve_object_knowledge",
            description: `Tra cứu tài liệu kỹ thuật cho một thiết bị/hiện vật cụ thể đang hiển thị trong tour 360°.
GỌI tool này KHI VÀ CHỈ KHI:
1. SPATIAL_CONTEXT cho biết có focusedObjectId (người dùng đang nhìn thẳng vào 1 object)
2. Người dùng hỏi về object đó (tên, thông số, lịch sử, chức năng...)
KHÔNG gọi khi người dùng hỏi chung chung về khung cảnh hoặc không có focusedObjectId.
Tool trả về: metadata hotspot + tài liệu kỹ thuật từ kho dữ liệu (RAG). Dùng kết quả này để trả lời chính xác, KHÔNG tự đoán thông số.`,
            parameters: {
                type: "OBJECT",
                properties: {
                    object_id: {
                        type: "STRING",
                        description: "ID của hotspot/thiết bị từ SPATIAL_CONTEXT.focusedObjectId (ví dụ: 'hs_42', 'hotspot_15')",
                    },
                    query: {
                        type: "STRING",
                        description: "Câu hỏi của người dùng về object (ví dụ: 'Thiết bị này có công suất bao nhiêu?')",
                    },
                },
                required: ["object_id", "query"],
            },
        },
        handler: async (args: any) => {
            console.log('🤖 retrieve_object_knowledge called with:', args);

            const idPath = typeof window !== 'undefined' ? window?.id_path : null;
            if (!idPath) {
                return {
                    success: false,
                    message: 'Cannot retrieve object knowledge: no project context (id_path missing)',
                };
            }

            try {
                const result = await functionCallingServices.retrieveObjectKnowledge(
                    idPath,
                    args.object_id,
                    args.query,
                    null, // scene_id — auto-detected by spatial context
                );

                if (result?.success && result?.data) {
                    const { hotspot, knowledge, source } = result.data;

                    // Format structured response for Gemini to use in its answer
                    let response = '';

                    if (hotspot) {
                        response += `HOTSPOT_METADATA:\n`;
                        response += `- ID: ${hotspot.id}\n`;
                        if (hotspot.title) response += `- Tên: ${hotspot.title}\n`;
                        if (hotspot.type_destination) response += `- Loại: ${hotspot.type_destination}\n`;
                        if (hotspot.scene_name) response += `- Vị trí: ${hotspot.scene_name}\n`;
                        response += '\n';
                    }

                    if (knowledge) {
                        response += `TECHNICAL_DOCUMENTATION:\n${knowledge}\n`;
                    }

                    if (!hotspot && !knowledge) {
                        response += `Không tìm thấy tài liệu cho object "${args.object_id}". Hãy cho người dùng biết là chưa có tài liệu kỹ thuật cho thiết bị này trong hệ thống.`;
                    }
                    return {
                        success: true,
                        message: 'Success',
                        source,
                        data: response,
                    };
                }

                return {
                    success: false,
                    message: (result as any)?.message || 'No data returned from knowledge retrieval',
                };
            } catch (err: any) {
                console.error('[retrieve_object_knowledge] Error:', err);
                return {
                    success: false,
                    message: `Knowledge retrieval failed: ${err?.message || 'unknown error'}`,
                };
            }
        },
    },

};

// ==================== MAP MODE TOOLS DEFINITIONS ====================
export const mapModeTools: Record<string, GeminiTool> = {
    filter_map_projects: portalTools.filter_map_projects,
    focus_map_project: portalTools.focus_map_project,
    open_map_directions: portalTools.open_map_directions,
    close_map_directions: portalTools.close_map_directions,
    get_sectors: portalTools.get_sectors,
    get_projects_by_location: portalTools.get_projects_by_location,
    open_project: portalTools.open_project,
    close_map: portalTools.close_map,
    close_tab: tools.close_tab,
    switch_province_mode: portalTools.switch_province_mode,
    reset_map_filter: portalTools.reset_map_filter,
};

// ==================== HANDLE TOOL CALL ====================
export const handleToolCall = async (functionCall: any, customTools?: Record<string, GeminiTool>) => {
    const activeTools = customTools || tools;
    const fn = activeTools[functionCall.name as keyof typeof activeTools];
    if (fn) {
        try {
            const result = await fn.handler(functionCall.args);
            return {
                id: functionCall.id,
                name: functionCall.name,
                response: { output: result }
            };
        } catch (error: any) {
            console.error(`Error executing tool ${functionCall.name}:`, error);
            return {
                id: functionCall.id,
                name: functionCall.name,
                response: { error: `Error executing ${functionCall.name}: ${error?.message || error}` }
            };
        }
    }
    return {
        id: functionCall.id,
        name: functionCall.name,
        response: { error: `Function ${functionCall.name} not found` }
    };
};
